import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSend, FiArrowLeft, FiEye, FiXCircle } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { hasCardAccess, hasCardAction } from '../utils/permissionHelper';
import SignaturePadModal from './common/SignaturePadModal';
import axiosInstance from '../api/axiosConfig';
import receptionBg from '../assets/images/backgrounds/reception-bg.png';
import './Cashbook.css';

// Numbers must end in a letter/digit — users used to append "." etc. to dodge
// the duplicate check. Returned vouchers can be re-issued with the same number.
const hasTrailingExtra = (value) => /[^A-Za-z0-9]$/.test((value || '').trim());
const TRAILING_EXTRA_MSG = 'Voucher number must not have extra characters (. , - etc.) at the end. Returned vouchers can be re-issued with the same number.';

const Cashbook = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const pendingTableRef = useRef(null);
    const allCashbookTableRef = useRef(null);

    // Permission-based access control
    const userPermissions = user?.permissions || { cards: [], actions: [] };
    const hasCashbookAccess = user?.role === 'system_admin' || hasCardAccess(userPermissions, 'cashbook');
    const canApprove = user?.role === 'system_admin' || hasCardAction(userPermissions, 'cashbook', 'approve');
    const canCreate = user?.role === 'system_admin' || hasCardAction(userPermissions, 'cashbook', 'create');
    const canEdit = user?.role === 'system_admin' || hasCardAction(userPermissions, 'cashbook', 'edit');

    const [activeTab, setActiveTab] = useState(
        canCreate && canApprove ? 'issue' : 'pending'
    );
    
    // STEP 1: Submit Cash Payment State (Accounts)
    const [issueForm, setIssueForm] = useState({
        voucherNumber: '', supplierName: '', company: '', amount: ''
    });
    const [issueLoading, setIssueLoading] = useState(false);
    const [issueError, setIssueError] = useState('');
    const [issueSuccess, setIssueSuccess] = useState('');
    
    // STEP 2: Pending Issuance State (Reception)
    const [pendingCashPayments, setPendingCashPayments] = useState([]);
    const [loadingPending, setLoadingPending] = useState(false);
    const [selectedCashPayment, setSelectedCashPayment] = useState(null);
    const [showCollectModal, setShowCollectModal] = useState(false);
    const [collectCheckingId, setCollectCheckingId] = useState(null);
    const [showReturnModal, setShowReturnModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [returnReason, setReturnReason] = useState('');
    const [cancellationReason, setCancellationReason] = useState('');
    const [returnLoading, setReturnLoading] = useState(false);
    const [cancelLoading, setCancelLoading] = useState(false);
    const [returnError, setReturnError] = useState('');
    const [cancelError, setCancelError] = useState('');
    const [collectForm, setCollectForm] = useState({
        receiptNumber: '', receiverName: '', contactNumber: '', complaints: ''
    });
    const [receiverSig, setReceiverSig] = useState(null);
    const [collectLoading, setCollectLoading] = useState(false);
    const [collectError, setCollectError] = useState('');
    
    // Pending Issuance Filters
    const [pendingSearchTerm, setPendingSearchTerm] = useState('');
    const [pendingCompanyFilter, setPendingCompanyFilter] = useState('');
    const [pendingSupplierFilter, setPendingSupplierFilter] = useState('');
    const [pendingFromDate, setPendingFromDate] = useState('');
    const [pendingToDate, setPendingToDate] = useState('');
    
    // View All State
    const [allCashPayments, setAllCashPayments] = useState([]);
    const [loadingAll, setLoadingAll] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [companyFilter, setCompanyFilter] = useState('');
    const [supplierFilter, setSupplierFilter] = useState('');
    const [suppliersList, setSuppliersList] = useState([]);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [viewModal, setViewModal] = useState(null);

    // Attachment State
    const [attachmentUploading, setAttachmentUploading] = useState(false);
    const attachmentInputRef = useRef(null);
    const [missingAttachments, setMissingAttachments] = useState(new Set());

    // Group By State
    const [viewMode, setViewMode] = useState('list');
    const [groupByField, setGroupByField] = useState('company');
    const [groupedData, setGroupedData] = useState([]);
    const [expandedGroups, setExpandedGroups] = useState(new Set());
    const [groupDetails, setGroupDetails] = useState({}); // Store drill-down data per group
    const [groupPages, setGroupPages] = useState({}); // Track current page per group
    const [loadingGroups, setLoadingGroups] = useState(new Set()); // Track loading state per group
    
    // Bulk Upload State
    const [bulkUploadFile, setBulkUploadFile] = useState(null);
    const [bulkUploadCompany, setBulkUploadCompany] = useState('');
    const [bulkUploadLoading, setBulkUploadLoading] = useState(false);
    const [bulkUploadError, setBulkUploadError] = useState('');
    const [bulkUploadSuccess, setBulkUploadSuccess] = useState('');
    // Optional attachments picked on the manual form, and the entries just
    // created by a bulk upload (so each can get its documents straight away)
    const [issueFiles, setIssueFiles] = useState([]);
    const issueFilesRef = useRef(null);
    const [bulkCreated, setBulkCreated] = useState([]);
    const [bulkAttachingId, setBulkAttachingId] = useState(null);
    const fileInputRef = useRef(null);
    
    // Bulk Selection State
    const [selectedCashPayments, setSelectedCashPayments] = useState(new Set());
    const [showBulkCollectModal, setShowBulkCollectModal] = useState(false);
    const [showBulkReturnModal, setShowBulkReturnModal] = useState(false);
    const [showBulkCancelModal, setShowBulkCancelModal] = useState(false);
    const [showBulkReissueModal, setShowBulkReissueModal] = useState(false);
    const [bulkCollectForm, setBulkCollectForm] = useState({
        receiptNumber: '', receiverName: '', contactNumber: '', complaints: ''
    });
    const [bulkReceiverSig, setBulkReceiverSig] = useState(null);
    const [bulkReturnReason, setBulkReturnReason] = useState('');
    const [bulkCancelReason, setBulkCancelReason] = useState('');
    const [bulkActionLoading, setBulkActionLoading] = useState(false);
    const [bulkActionProgress, setBulkActionProgress] = useState({ current: 0, total: 0 });
    const [bulkActionResults, setBulkActionResults] = useState({ success: [], failed: [] });
    
    // Autocomplete State
    const [supplierSuggestions, setSupplierSuggestions] = useState([]);
    const [showSupplierSuggestions, setShowSupplierSuggestions] = useState(false);
    const [loadingSupplierSuggestions, setLoadingSupplierSuggestions] = useState(false);
    const [receiverSuggestions, setReceiverSuggestions] = useState([]);
    const [showReceiverSuggestions, setShowReceiverSuggestions] = useState(false);
    const [loadingReceiverSuggestions, setLoadingReceiverSuggestions] = useState(false);
    // Debounce timers + request counters so out-of-order responses never
    // overwrite newer suggestions or reopen the dropdown after selection
    const supplierDebounceRef = useRef(null);
    const supplierRequestRef = useRef(0);
    const receiverDebounceRef = useRef(null);
    const receiverRequestRef = useRef(0);
    const [bulkReceiverSuggestions, setBulkReceiverSuggestions] = useState([]);
    const [showBulkReceiverSuggestions, setShowBulkReceiverSuggestions] = useState(false);
    const [loadingBulkReceiverSuggestions, setLoadingBulkReceiverSuggestions] = useState(false);
    const bulkReceiverDebounceRef = useRef(null);
    const bulkReceiverRequestRef = useRef(0);

    // ========== STEP 1: SUBMIT CASH PAYMENT FUNCTIONS (ACCOUNTS) ==========
    const handleIssueChange = (e) => {
        const { name, value } = e.target;
        setIssueForm({ ...issueForm, [name]: value });
        setIssueError('');

        // Trigger autocomplete for supplier name
        if (name === 'supplierName') {
            clearTimeout(supplierDebounceRef.current);
            const searchTerm = value.trim();
            if (searchTerm.length >= 2) {
                supplierDebounceRef.current = setTimeout(() => {
                    fetchSupplierSuggestions(searchTerm);
                }, 300);
            } else {
                supplierRequestRef.current += 1; // invalidate any in-flight request
                setSupplierSuggestions([]);
                setShowSupplierSuggestions(false);
                setLoadingSupplierSuggestions(false);
            }
        }
    };

    const fetchSupplierSuggestions = async (searchTerm) => {
        const requestId = ++supplierRequestRef.current;
        setLoadingSupplierSuggestions(true);
        try {
            const response = await axiosInstance.get(
                `/api/cashbook/autocomplete?search=${encodeURIComponent(searchTerm)}`
            );
            if (requestId !== supplierRequestRef.current) return; // stale response
            if (response.data.success) {
                setSupplierSuggestions(response.data.data);
                setShowSupplierSuggestions(response.data.data.length > 0);
            }
        } catch (err) {
            console.error('Error fetching supplier suggestions:', err);
        } finally {
            if (requestId === supplierRequestRef.current) {
                setLoadingSupplierSuggestions(false);
            }
        }
    };

    const handleSelectSupplier = (suggestion) => {
        clearTimeout(supplierDebounceRef.current);
        supplierRequestRef.current += 1; // ignore any in-flight request
        setIssueForm({ ...issueForm, supplierName: suggestion.supplierName });
        setShowSupplierSuggestions(false);
        setSupplierSuggestions([]);
        setLoadingSupplierSuggestions(false);
    };
    
    // Close suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest('.autocomplete-wrapper')) {
                setShowSupplierSuggestions(false);
                setShowReceiverSuggestions(false);
                setShowBulkReceiverSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            clearTimeout(supplierDebounceRef.current);
            clearTimeout(receiverDebounceRef.current);
            clearTimeout(bulkReceiverDebounceRef.current);
        };
    }, []);
    
    // Horizontal scroll with mouse wheel and keyboard
    useEffect(() => {
        const handleHorizontalScroll = (e, container) => {
            if (!container) return;
            
            // Mouse wheel horizontal scroll - only when Shift is pressed
            if (e.type === 'wheel' && e.shiftKey && Math.abs(e.deltaX) === 0) {
                e.preventDefault();
                container.scrollLeft += e.deltaY;
            }
        };
        
        const handleKeyboardScroll = (e, container) => {
            if (!container) return;
            
            // Arrow keys for horizontal scroll
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                container.scrollLeft -= 50;
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                container.scrollLeft += 50;
            }
        };
        
        const pendingTable = pendingTableRef.current;
        const allCashbookTable = allCashbookTableRef.current;
        
        // Add event listeners for pending table
        if (pendingTable) {
            const wheelHandler = (e) => handleHorizontalScroll(e, pendingTable);
            const keyHandler = (e) => handleKeyboardScroll(e, pendingTable);
            
            pendingTable.addEventListener('wheel', wheelHandler, { passive: false });
            pendingTable.addEventListener('keydown', keyHandler);
            pendingTable.setAttribute('tabindex', '0');
            
            return () => {
                pendingTable.removeEventListener('wheel', wheelHandler);
                pendingTable.removeEventListener('keydown', keyHandler);
            };
        }
        
        // Add event listeners for all cashbook table
        if (allCashbookTable) {
            const wheelHandler = (e) => handleHorizontalScroll(e, allCashbookTable);
            const keyHandler = (e) => handleKeyboardScroll(e, allCashbookTable);
            
            allCashbookTable.addEventListener('wheel', wheelHandler, { passive: false });
            allCashbookTable.addEventListener('keydown', keyHandler);
            allCashbookTable.setAttribute('tabindex', '0');
            
            return () => {
                allCashbookTable.removeEventListener('wheel', wheelHandler);
                allCashbookTable.removeEventListener('keydown', keyHandler);
            };
        }
    }, [activeTab, pendingCashPayments, allCashPayments]);
    
    const handleIssueCashPayment = async (e) => {
        e.preventDefault();
        if (!issueForm.voucherNumber || !issueForm.supplierName || !issueForm.company || !issueForm.amount) {
            setIssueError('All fields are required');
            return;
        }
        if (hasTrailingExtra(issueForm.voucherNumber)) {
            setIssueError(TRAILING_EXTRA_MSG);
            return;
        }
        
        // Auto-generate accountant digital signature
        const accountantSignature = `Digitally signed by ${user?.fullName || user?.username}`;
        
        setIssueLoading(true);
        setIssueError('');
        try {
            const response = await axiosInstance.post('/api/cashbook', 
                { ...issueForm, accountantSignature: accountantSignature }
            );
            if (response.data.success) {
                let message = 'Cash payment submitted successfully!';
                if (issueFiles.length > 0) {
                    try {
                        await uploadEntryDocuments(response.data.data._id, issueFiles);
                        message = `Cash payment submitted with ${issueFiles.length} attachment(s)!`;
                    } catch (uploadErr) {
                        setIssueError(`Cash payment was saved, but the attachment failed to upload (${uploadErr.response?.data?.message || uploadErr.message}). Please add it from View.`);
                    }
                }
                setIssueSuccess(message);
                setIssueForm({ voucherNumber: '', supplierName: '', company: '', amount: '' });
                setIssueFiles([]);
                if (issueFilesRef.current) issueFilesRef.current.value = '';
                setTimeout(() => setIssueSuccess(''), 3000);
            }
        } catch (err) {
            setIssueError(err.response?.data?.message || 'Failed to submit cash payment');
        } finally {
            setIssueLoading(false);
        }
    };

    // Attach files to an existing entry (same endpoint as the View modal)
    const uploadEntryDocuments = async (id, files) => {
        const formData = new FormData();
        files.forEach(file => formData.append('files', file));
        const response = await axiosInstance.post(
            `/api/cashbook/${id}/documents`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        return response.data.documents || [];
    };

    // Attach files to one row of the "just uploaded" list after a bulk upload
    const handleBulkAttach = async (entryId, e) => {
        const files = Array.from(e.target.files);
        e.target.value = '';
        if (!files.length) return;
        const setCount = (count) => setBulkCreated(prev => prev.map(r => r._id === entryId ? { ...r, docCount: count } : r));
        setBulkAttachingId(entryId);
        try {
            setCount((await uploadEntryDocuments(entryId, files)).length);
            fetchAllCashPayments();
        } catch (err) {
            const partial = err.response?.data?.documents;
            if (partial) setCount(partial.length);
            alert(err.response?.data?.message || 'Failed to upload files');
        } finally {
            setBulkAttachingId(null);
        }
    };

    // ========== BULK UPLOAD FUNCTIONS ==========
    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            const validTypes = ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv'];
            const validExtensions = ['.xls', '.xlsx', '.csv'];
            const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
            
            if (validTypes.includes(file.type) || validExtensions.includes(fileExtension)) {
                setBulkUploadFile(file);
                setBulkUploadError('');
            } else {
                setBulkUploadError('Please select a valid Excel file (.xls, .xlsx, or .csv)');
                setBulkUploadFile(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
            }
        }
    };
    
    const handleBulkUpload = async () => {
        if (!bulkUploadFile) {
            setBulkUploadError('Please select a file first');
            return;
        }
        
        if (!bulkUploadCompany) {
            setBulkUploadError('Please select a company first');
            return;
        }
        
        setBulkUploadLoading(true);
        setBulkUploadError('');
        setBulkUploadSuccess('');
        setBulkCreated([]);

        try {
            const formData = new FormData();
            formData.append('file', bulkUploadFile);
            formData.append('company', bulkUploadCompany);
            
            const response = await axiosInstance.post(
                '/api/cashbook/bulk-upload',
                formData
            );
            
            if (response.data.success) {
                const { summary, details } = response.data;
                
                // Build detailed success message
                let successMessage = `Successfully uploaded ${summary.uploaded} cashbook entries for ${bulkUploadCompany}!`;
                
                if (summary.skipped > 0) {
                    successMessage += `\n\n📋 Summary:\n`;
                    successMessage += `✅ Uploaded: ${summary.uploaded}\n`;
                    successMessage += `⏭️ Skipped (duplicates): ${summary.skipped}\n`;
                    
                    if (summary.errors > 0) {
                        successMessage += `❌ Validation errors: ${summary.errors}\n`;
                    }
                    
                    // Show details of skipped items
                    if (details.skippedDuplicatesInDatabase?.length > 0) {
                        successMessage += `\n🔄 Skipped (already in database):\n`;
                        details.skippedDuplicatesInDatabase.slice(0, 5).forEach(item => {
                            successMessage += `  • Row ${item.row}: ${item.voucherNumber} - ${item.supplierName}\n`;
                        });
                        if (details.skippedDuplicatesInDatabase.length > 5) {
                            successMessage += `  ... and ${details.skippedDuplicatesInDatabase.length - 5} more\n`;
                        }
                    }
                    
                    if (details.skippedDuplicatesInFile?.length > 0) {
                        successMessage += `\n📄 Skipped (duplicates in file):\n`;
                        details.skippedDuplicatesInFile.slice(0, 5).forEach(item => {
                            successMessage += `  • Row ${item.row}: ${item.voucherNumber} - ${item.supplierName}\n`;
                        });
                        if (details.skippedDuplicatesInFile.length > 5) {
                            successMessage += `  ... and ${details.skippedDuplicatesInFile.length - 5} more\n`;
                        }
                    }
                }

                // Rows that failed (validation or rejected by the database) — shown
                // even when nothing was skipped, so a partial upload is never silent
                if (details.validationErrors?.length > 0) {
                    successMessage += `\n❌ Not uploaded (${details.validationErrors.length}):\n`;
                    details.validationErrors.forEach(item => {
                        successMessage += `  • Row ${item.row}: ${item.voucherNumber} - ${item.error}\n`;
                    });
                }

                setBulkUploadSuccess(successMessage);
                setBulkCreated((details.uploadedEntries || []).map(e => ({
                    _id: e._id, voucherNumber: e.voucherNumber, supplierName: e.supplierName,
                    amount: e.amount, docCount: (e.documents || []).length
                })));
                setBulkUploadFile(null);
                setBulkUploadCompany('');
                if (fileInputRef.current) fileInputRef.current.value = '';
                
                // Refresh the cashbook list
                fetchPending();
                fetchAllCashPayments();
                
                // Clear success message after 10 seconds (longer for detailed messages)
                setTimeout(() => setBulkUploadSuccess(''), 10000);
            }
        } catch (err) {
            const errorMsg = err.response?.data?.message || 'Failed to upload file';
            const details = err.response?.data?.details;
            
            let fullErrorMessage = errorMsg;
            
            // Add validation errors if present
            if (details?.validationErrors && details.validationErrors.length > 0) {
                fullErrorMessage += '\n\n❌ Validation Errors:\n';
                details.validationErrors.forEach(error => {
                    fullErrorMessage += `  • Row ${error.row}: ${error.voucherNumber} - ${error.error}\n`;
                });
            }
            
            // Add skipped duplicates info if present
            if (details?.skippedDuplicatesInDatabase && details.skippedDuplicatesInDatabase.length > 0) {
                fullErrorMessage += '\n\n⏭️ Skipped (already in database):\n';
                details.skippedDuplicatesInDatabase.slice(0, 5).forEach(item => {
                    fullErrorMessage += `  • Row ${item.row}: ${item.voucherNumber} - ${item.reason}\n`;
                });
                if (details.skippedDuplicatesInDatabase.length > 5) {
                    fullErrorMessage += `  ... and ${details.skippedDuplicatesInDatabase.length - 5} more\n`;
                }
            }
            
            if (details?.skippedDuplicatesInFile && details.skippedDuplicatesInFile.length > 0) {
                fullErrorMessage += '\n\n📄 Skipped (duplicates in file):\n';
                details.skippedDuplicatesInFile.slice(0, 5).forEach(item => {
                    fullErrorMessage += `  • Row ${item.row}: ${item.voucherNumber} - ${item.reason}\n`;
                });
                if (details.skippedDuplicatesInFile.length > 5) {
                    fullErrorMessage += `  ... and ${details.skippedDuplicatesInFile.length - 5} more\n`;
                }
            }
            
            setBulkUploadError(fullErrorMessage);
        } finally {
            setBulkUploadLoading(false);
        }
    };
    
    const clearBulkUpload = () => {
        setBulkUploadFile(null);
        setBulkUploadCompany('');
        setBulkUploadError('');
        setBulkUploadSuccess('');
        setBulkCreated([]);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };


    // ========== STEP 2: ISSUE TO SUPPLIER FUNCTIONS (RECEPTION) ==========
    const fetchPending = async (search = '', company = '', supplier = '', fromDate = '', toDate = '') => {
        setLoadingPending(true);
        try {
            const response = await axiosInstance.get(
                `/api/cashbook?status=pending&limit=100&search=${search}&company=${company}&supplier=${supplier}&fromDate=${fromDate}&toDate=${toDate}`
            );
            if (response.data.success) setPendingCashPayments(response.data.data);
        } catch (err) {
            console.error('Error fetching pending:', err);
        } finally {
            setLoadingPending(false);
        }
    };
    
    const handlePendingSearch = (e) => {
        setPendingSearchTerm(e.target.value);
        fetchPending(e.target.value, pendingCompanyFilter, pendingSupplierFilter, pendingFromDate, pendingToDate);
    };
    
    const handlePendingCompanyFilter = (company) => {
        setPendingCompanyFilter(company);
        fetchPending(pendingSearchTerm, company, pendingSupplierFilter, pendingFromDate, pendingToDate);
    };

    const handlePendingSupplierFilter = (supplier) => {
        setPendingSupplierFilter(supplier);
        fetchPending(pendingSearchTerm, pendingCompanyFilter, supplier, pendingFromDate, pendingToDate);
    };
    
    const openCollectModal = async (cashPayment) => {
        // Verify the cash payment is still collectable BEFORE opening the form, so
        // the receptionist doesn't fill in receipt/receiver details and capture the
        // recipient's signature only to be rejected on submit. A cancel + re-issue
        // by Accounts leaves this pending list pointing at the old record.
        setCollectCheckingId(cashPayment._id);
        try {
            const { data } = await axiosInstance.get(`/api/cashbook/${cashPayment._id}`);
            const current = data?.data;
            if (!current || current.status !== 'pending') {
                const statusMsg = {
                    collected: 'This cash payment has already been collected.',
                    cancelled: 'This cash payment was cancelled by Accounts. If it was re-issued, collect the new entry.',
                    returned: 'This cash payment was returned to Accounts and can no longer be collected.'
                }[current?.status] || 'This cash payment is no longer available for collection.';
                alert(`${statusMsg}\n\nThe list will now refresh.`);
                fetchPending(pendingSearchTerm, pendingCompanyFilter, pendingSupplierFilter, pendingFromDate, pendingToDate);
                return;
            }
            // Use the freshly fetched record so the form reflects current data.
            setSelectedCashPayment(current);
            setShowCollectModal(true);
            setCollectForm({ receiptNumber: '', receiverName: '', contactNumber: '', complaints: '' });
            setReceiverSig(null);
            setCollectError('');
        } catch (err) {
            if (err.response?.status === 404) {
                alert('This cash payment no longer exists. The list will now refresh.');
                fetchPending(pendingSearchTerm, pendingCompanyFilter, pendingSupplierFilter, pendingFromDate, pendingToDate);
            } else {
                alert('Could not verify the cash payment status. Please check your connection and try again.');
            }
        } finally {
            setCollectCheckingId(null);
        }
    };
    
    const openReturnModal = (cashPayment) => {
        setSelectedCashPayment(cashPayment);
        setShowReturnModal(true);
        setReturnReason('');
        setReturnError('');
    };
    
    const openCancelModal = (cashPayment) => {
        setSelectedCashPayment(cashPayment);
        setShowCancelModal(true);
        setCancellationReason('');
        setCancelError('');
    };
    
    const closeCollectModal = () => {
        setShowCollectModal(false);
        setSelectedCashPayment(null);
        setReceiverSig(null);
    };
    
    const closeReturnModal = () => {
        setShowReturnModal(false);
        setSelectedCashPayment(null);
        setReturnReason('');
        setReturnError('');
    };
    
    const closeCancelModal = () => {
        setShowCancelModal(false);
        setSelectedCashPayment(null);
        setCancellationReason('');
        setCancelError('');
    };
    
    const handleCollectChange = (e) => {
        const { name, value } = e.target;
        setCollectForm({ ...collectForm, [name]: value });
        setCollectError('');
        
        // Trigger autocomplete for receiver based on what user is typing
        if (name === 'receiverName') {
            clearTimeout(receiverDebounceRef.current);
            const searchTerm = value.trim();
            if (searchTerm.length >= 2) {
                receiverDebounceRef.current = setTimeout(() => {
                    fetchReceiverSuggestions(searchTerm, selectedCashPayment?.supplierName);
                }, 300);
            } else {
                receiverRequestRef.current += 1; // invalidate any in-flight request
                setReceiverSuggestions([]);
                setShowReceiverSuggestions(false);
                setLoadingReceiverSuggestions(false);
            }
        }
    };

    const fetchReceiverSuggestions = async (searchTerm, supplierName) => {
        const requestId = ++receiverRequestRef.current;
        setLoadingReceiverSuggestions(true);
        try {
            const response = await axiosInstance.get(
                `/api/cashbook/autocomplete/receiver?search=${encodeURIComponent(searchTerm)}&supplier=${encodeURIComponent(supplierName || '')}`
            );
            if (requestId !== receiverRequestRef.current) return; // stale response
            if (response.data.success) {
                setReceiverSuggestions(response.data.data);
                setShowReceiverSuggestions(response.data.data.length > 0);
            }
        } catch (err) {
            console.error('Error fetching receiver suggestions:', err);
        } finally {
            if (requestId === receiverRequestRef.current) {
                setLoadingReceiverSuggestions(false);
            }
        }
    };

    const handleSelectReceiver = (suggestion) => {
        clearTimeout(receiverDebounceRef.current);
        receiverRequestRef.current += 1; // ignore any in-flight request
        setCollectForm({
            ...collectForm,
            receiverName: suggestion.receiverName,
            contactNumber: suggestion.contactNumber
        });
        setShowReceiverSuggestions(false);
        setReceiverSuggestions([]);
        setLoadingReceiverSuggestions(false);
    };

    const handleBulkCollectChange = (e) => {
        const { name, value } = e.target;
        setBulkCollectForm({ ...bulkCollectForm, [name]: value });

        if (name === 'receiverName') {
            clearTimeout(bulkReceiverDebounceRef.current);
            const searchTerm = value.trim();
            if (searchTerm.length >= 2) {
                bulkReceiverDebounceRef.current = setTimeout(() => {
                    fetchBulkReceiverSuggestions(searchTerm);
                }, 300);
            } else {
                bulkReceiverRequestRef.current += 1; // invalidate any in-flight request
                setBulkReceiverSuggestions([]);
                setShowBulkReceiverSuggestions(false);
                setLoadingBulkReceiverSuggestions(false);
            }
        }
    };

    const fetchBulkReceiverSuggestions = async (searchTerm) => {
        const requestId = ++bulkReceiverRequestRef.current;
        setLoadingBulkReceiverSuggestions(true);
        try {
            const response = await axiosInstance.get(
                `/api/cashbook/autocomplete/receiver?search=${encodeURIComponent(searchTerm)}`
            );
            if (requestId !== bulkReceiverRequestRef.current) return; // stale response
            if (response.data.success) {
                setBulkReceiverSuggestions(response.data.data);
                setShowBulkReceiverSuggestions(response.data.data.length > 0);
            }
        } catch (err) {
            console.error('Error fetching receiver suggestions:', err);
        } finally {
            if (requestId === bulkReceiverRequestRef.current) {
                setLoadingBulkReceiverSuggestions(false);
            }
        }
    };

    const handleSelectBulkReceiver = (suggestion) => {
        clearTimeout(bulkReceiverDebounceRef.current);
        bulkReceiverRequestRef.current += 1; // ignore any in-flight request
        setBulkCollectForm({
            ...bulkCollectForm,
            receiverName: suggestion.receiverName,
            contactNumber: suggestion.contactNumber
        });
        setShowBulkReceiverSuggestions(false);
        setBulkReceiverSuggestions([]);
        setLoadingBulkReceiverSuggestions(false);
    };

    const handleCollectCashPayment = async (e) => {
        e.preventDefault();
        if (!collectForm.receiptNumber || !collectForm.receiverName || !collectForm.contactNumber) {
            setCollectError('All fields are required');
            return;
        }

        const finalReceiverSig = receiverSig;
        if (!finalReceiverSig) {
            setCollectError('Receiver signature is required');
            return;
        }
        
        // Generate receptionist digital signature automatically
        const receptionistSignature = `Digitally signed by ${user?.fullName || user?.username}`;
        
        setCollectLoading(true);
        setCollectError('');
        try {
            const response = await axiosInstance.patch(
                `/api/cashbook/${selectedCashPayment._id}/collect`,
                { ...collectForm, receiverSignature: finalReceiverSig, receptionistSignature: receptionistSignature }
            );
            if (response.data.success) {
                closeCollectModal();
                fetchPending(pendingSearchTerm, pendingCompanyFilter, pendingSupplierFilter, pendingFromDate, pendingToDate); // Refresh list
                alert('Cash payment issued to supplier successfully!');
            }
        } catch (err) {
            setCollectError(err.response?.data?.message || 'Failed to issue cash payment to supplier');
        } finally {
            setCollectLoading(false);
        }
    };
    
    const handleReturnCashPayment = async (e) => {
        e.preventDefault();
        
        setReturnLoading(true);
        setReturnError('');
        try {
            const response = await axiosInstance.patch(
                `/api/cashbook/${selectedCashPayment._id}/return`,
                { returnReason: returnReason.trim() }
            );
            if (response.data.success) {
                closeReturnModal();
                fetchPending(pendingSearchTerm, pendingCompanyFilter, pendingSupplierFilter, pendingFromDate, pendingToDate); // Refresh list
                alert('Cash payment returned to accounts successfully!');
            }
        } catch (err) {
            setReturnError(err.response?.data?.message || 'Failed to return cash payment');
        } finally {
            setReturnLoading(false);
        }
    };
    
    const handleCancelCashPayment = async (e) => {
        e.preventDefault();
        
        setCancelLoading(true);
        setCancelError('');
        try {
            const response = await axiosInstance.patch(
                `/api/cashbook/${selectedCashPayment._id}/cancel`,
                { cancellationReason: cancellationReason.trim() }
            );
            if (response.data.success) {
                closeCancelModal();
                setViewModal(null);
                // Refresh the list
                if (viewMode === 'list') {
                    fetchAllCashPayments(currentPage, searchTerm, statusFilter, companyFilter, supplierFilter, fromDate, toDate);
                } else {
                    fetchGroupedCashbook(groupByField);
                }
                alert('Cash payment cancelled successfully!');
            }
        } catch (err) {
            setCancelError(err.response?.data?.message || 'Failed to cancel cash payment');
        } finally {
            setCancelLoading(false);
        }
    };

    // Re-issue a returned/cancelled cash payment
    const handleReissueCashPayment = async (cashPaymentId) => {
        if (!window.confirm('Re-submit this cash payment with the same details? A new entry will be created with status "Pending Issuance".')) {
            return;
        }

        try {
            const response = await axiosInstance.post(
                `/api/cashbook/${cashPaymentId}/reissue`,
                {}
            );

            if (response.data.success) {
                alert('✅ Cash payment re-submitted successfully!');
                setViewModal(null);
                // Refresh the list
                if (viewMode === 'list') {
                    fetchAllCashPayments(currentPage, searchTerm, statusFilter, companyFilter, supplierFilter, fromDate, toDate);
                } else {
                    fetchGroupedCashbook(groupByField);
                }
            }
        } catch (err) {
            console.error('Error re-issuing cash payment:', err);
            alert(err.response?.data?.message || 'Failed to re-submit cash payment');
        }
    };

    // ========== ATTACHMENT FUNCTIONS ==========
    const handleAttachmentUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length || !viewModal) return;

        const formData = new FormData();
        files.forEach(file => formData.append('files', file));

        setAttachmentUploading(true);
        try {
            const response = await axiosInstance.post(
                `/api/cashbook/${viewModal._id}/documents`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            if (response.data.success) {
                const updatedDocs = response.data.documents;
                setViewModal(prev => ({ ...prev, documents: updatedDocs }));
                setAllCashPayments(prev => prev.map(p =>
                    p._id === viewModal._id ? { ...p, documents: updatedDocs } : p
                ));
            }
        } catch (err) {
            // A part-way failure still returns the documents that did upload —
            // reflect them so the list matches what is actually in SharePoint
            // and the user knows which files to retry.
            const partial = err.response?.data?.documents;
            if (partial) {
                setViewModal(prev => ({ ...prev, documents: partial }));
                setAllCashPayments(prev => prev.map(p =>
                    p._id === viewModal._id ? { ...p, documents: partial } : p
                ));
            }
            alert(err.response?.data?.message || 'Failed to upload files');
        } finally {
            setAttachmentUploading(false);
            if (attachmentInputRef.current) attachmentInputRef.current.value = '';
        }
    };

    const handleDeleteAttachment = async (docId, fileName) => {
        if (!window.confirm(`Delete "${fileName}"?`)) return;
        try {
            const response = await axiosInstance.delete(
                `/api/cashbook/${viewModal._id}/documents/${docId}`
            );
            if (response.data.success) {
                const updatedDocs = response.data.documents;
                setViewModal(prev => ({ ...prev, documents: updatedDocs }));
                setAllCashPayments(prev => prev.map(p =>
                    p._id === viewModal._id ? { ...p, documents: updatedDocs } : p
                ));
            }
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to delete file');
        }
    };

    // Attachments live in SharePoint. The backend streams them through so the
    // app's card permissions stay in charge of access — hence a blob fetch
    // rather than opening doc.spWebUrl directly. Documents uploaded before the
    // SharePoint switch have storage === 'local' (or no storage field at all)
    // and are still served off disk.
    const handleViewAttachment = async (doc) => {
        const isLocal = (!doc.storage || doc.storage === 'local') && doc.fileUrl;
        const endpoint = isLocal
            ? `/api/files/cashbook/${doc.fileUrl.split('/').pop()}`
            : `/api/cashbook/${viewModal._id}/documents/${doc._id}/content`;
        const fileName = doc.fileName;

        try {
            const response = await axiosInstance.get(endpoint, { responseType: 'blob' });

            // Succeeded — clear any earlier "missing" flag, so restoring a file
            // from the SharePoint recycle bin and hitting 🔄 puts the row right.
            if (missingAttachments.has(doc._id)) {
                setMissingAttachments(prev => {
                    const next = new Set(prev);
                    next.delete(doc._id);
                    return next;
                });
            }

            const blob = new Blob([response.data], { type: response.data.type });
            const url = URL.createObjectURL(blob);

            if (response.data.type === 'application/pdf' || response.data.type.startsWith('image/')) {
                window.open(url, '_blank');
            } else {
                const a = document.createElement('a');
                a.href = url;
                a.download = fileName;
                a.click();
            }
            setTimeout(() => URL.revokeObjectURL(url), 10000);
        } catch (err) {
            // responseType is 'blob', so an error response arrives as a Blob too —
            // read it back as text to surface the backend's actual message
            // (expired secret, missing site permission, file removed in SharePoint).
            let message = 'Failed to open file';
            try {
                const text = await err.response?.data?.text?.();
                const parsed = text ? JSON.parse(text) : null;
                if (parsed?.message) message = parsed.message;
            } catch {
                // Not JSON — keep the generic message.
            }

            // 404 means the row outlived its file — someone deleted it straight
            // from SharePoint, so nothing told the database. Flag the row rather
            // than removing it: a read must not delete data, and the file may
            // still be restorable from the site recycle bin.
            if (err.response?.status === 404) {
                setMissingAttachments(prev => new Set(prev).add(doc._id));
            }
            alert(message);
        }
    };

    // Open a printable PDF voucher for a cash payment in a new tab
    const handleDownloadPdf = async (entry) => {
        if (!entry?._id) return;
        try {
            const response = await axiosInstance.get(
                `/api/cashbook/${entry._id}/pdf`,
                { responseType: 'blob' }
            );
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 10000);
        } catch (err) {
            alert('Failed to generate PDF');
        }
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    // ========== BULK SELECTION FUNCTIONS ==========
    const toggleCashPaymentSelection = (cashPaymentId) => {
        setSelectedCashPayments(prev => {
            const newSet = new Set(prev);
            if (newSet.has(cashPaymentId)) {
                newSet.delete(cashPaymentId);
            } else {
                newSet.add(cashPaymentId);
            }
            return newSet;
        });
    };

    const toggleSelectAll = () => {
        if (selectedCashPayments.size === pendingCashPayments.length) {
            setSelectedCashPayments(new Set());
        } else {
            setSelectedCashPayments(new Set(pendingCashPayments.map(c => c._id)));
        }
    };

    const openBulkCollectModal = () => {
        if (selectedCashPayments.size === 0) {
            alert('Please select at least one cash payment');
            return;
        }
        setShowBulkCollectModal(true);
        setBulkCollectForm({ receiptNumber: '', receiverName: '', contactNumber: '', complaints: '' });
        setBulkReceiverSig(null);
        setBulkActionResults({ success: [], failed: [] });
        setBulkReceiverSuggestions([]);
        setShowBulkReceiverSuggestions(false);
    };

    const openBulkReturnModal = () => {
        if (selectedCashPayments.size === 0) {
            alert('Please select at least one cash payment');
            return;
        }
        setShowBulkReturnModal(true);
        setBulkReturnReason('');
        setBulkActionResults({ success: [], failed: [] });
    };

    const openBulkCancelModal = () => {
        if (selectedCashPayments.size === 0) {
            alert('Please select at least one cash payment');
            return;
        }
        setShowBulkCancelModal(true);
        setBulkCancelReason('');
        setBulkActionResults({ success: [], failed: [] });
    };

    const openBulkReissueModal = () => {
        if (selectedCashPayments.size === 0) {
            alert('Please select at least one cash payment');
            return;
        }
        setShowBulkReissueModal(true);
        setBulkActionResults({ success: [], failed: [] });
    };

    const closeBulkCollectModal = () => {
        setShowBulkCollectModal(false);
        setBulkCollectForm({ receiptNumber: '', receiverName: '', contactNumber: '', complaints: '' });
        setBulkReceiverSig(null);
    };

    const closeBulkReturnModal = () => {
        setShowBulkReturnModal(false);
        setBulkReturnReason('');
    };

    const closeBulkCancelModal = () => {
        setShowBulkCancelModal(false);
        setBulkCancelReason('');
        setBulkActionResults({ success: [], failed: [] });
    };

    const closeBulkReissueModal = () => {
        setShowBulkReissueModal(false);
        setBulkActionResults({ success: [], failed: [] });
    };

    const handleBulkCollect = async (e) => {
        e.preventDefault();

        // Validate
        if (!bulkCollectForm.receiptNumber || !bulkCollectForm.receiverName || !bulkCollectForm.contactNumber) {
            alert('Please fill all required fields');
            return;
        }

        if (!bulkReceiverSig) {
            alert('Receiver signature is required');
            return;
        }

        // Generate receptionist digital signature automatically (same as single collect)
        const receptionistSignature = `Digitally signed by ${user?.fullName || user?.username}`;

        setBulkActionLoading(true);
        const results = { success: [], failed: [] };
        const cashPaymentsToCollect = Array.from(selectedCashPayments);
        setBulkActionProgress({ current: 0, total: cashPaymentsToCollect.length });

        for (let i = 0; i < cashPaymentsToCollect.length; i++) {
            const cashPaymentId = cashPaymentsToCollect[i];
            const cashPayment = pendingCashPayments.find(c => c._id === cashPaymentId);
            
            try {
                setBulkActionProgress({ current: i + 1, total: cashPaymentsToCollect.length });
                
                const response = await axiosInstance.patch(
                    `/api/cashbook/${cashPaymentId}/collect`,
                    {
                        receiptNumber: bulkCollectForm.receiptNumber,
                        receiverName: bulkCollectForm.receiverName,
                        contactNumber: bulkCollectForm.contactNumber,
                        complaints: bulkCollectForm.complaints,
                        receiverSignature: bulkReceiverSig,
                        receptionistSignature: receptionistSignature
                    }
                );
                
                if (response.data.success) {
                    results.success.push({
                        voucherNumber: cashPayment.voucherNumber,
                        supplierName: cashPayment.supplierName
                    });
                }
            } catch (err) {
                results.failed.push({
                    voucherNumber: cashPayment.voucherNumber,
                    supplierName: cashPayment.supplierName,
                    error: err.response?.data?.message || 'Failed to issue to supplier'
                });
            }
        }

        setBulkActionResults(results);
        setBulkActionLoading(false);
        
        // Show results
        const successCount = results.success.length;
        const failedCount = results.failed.length;
        let message = `Bulk Issuance Complete:\n✅ Success: ${successCount}\n❌ Failed: ${failedCount}`;
        
        if (failedCount > 0) {
            message += '\n\nFailed items:\n';
            results.failed.forEach(item => {
                message += `• ${item.voucherNumber} - ${item.error}\n`;
            });
        }
        
        alert(message);
        
        // Refresh and close
        fetchPending(pendingSearchTerm, pendingCompanyFilter, pendingSupplierFilter, pendingFromDate, pendingToDate);
        setSelectedCashPayments(new Set());
        closeBulkCollectModal();
    };

    const handleBulkReturn = async (e) => {
        e.preventDefault();

        setBulkActionLoading(true);
        const results = { success: [], failed: [] };
        const cashPaymentsToReturn = Array.from(selectedCashPayments);
        setBulkActionProgress({ current: 0, total: cashPaymentsToReturn.length });

        for (let i = 0; i < cashPaymentsToReturn.length; i++) {
            const cashPaymentId = cashPaymentsToReturn[i];
            const cashPayment = pendingCashPayments.find(c => c._id === cashPaymentId);
            
            try {
                setBulkActionProgress({ current: i + 1, total: cashPaymentsToReturn.length });
                
                const response = await axiosInstance.patch(
                    `/api/cashbook/${cashPaymentId}/return`,
                    { returnReason: bulkReturnReason.trim() }
                );
                
                if (response.data.success) {
                    results.success.push({
                        voucherNumber: cashPayment.voucherNumber,
                        supplierName: cashPayment.supplierName
                    });
                }
            } catch (err) {
                results.failed.push({
                    voucherNumber: cashPayment.voucherNumber,
                    supplierName: cashPayment.supplierName,
                    error: err.response?.data?.message || 'Failed to return'
                });
            }
        }

        setBulkActionResults(results);
        setBulkActionLoading(false);
        
        // Show results
        const successCount = results.success.length;
        const failedCount = results.failed.length;
        let message = `Bulk Return Complete:\n✅ Success: ${successCount}\n❌ Failed: ${failedCount}`;
        
        if (failedCount > 0) {
            message += '\n\nFailed items:\n';
            results.failed.forEach(item => {
                message += `• ${item.voucherNumber} - ${item.error}\n`;
            });
        }
        
        alert(message);
        
        // Refresh and close
        fetchPending(pendingSearchTerm, pendingCompanyFilter, pendingSupplierFilter, pendingFromDate, pendingToDate);
        setSelectedCashPayments(new Set());
        closeBulkReturnModal();
    };
    
    const handleBulkCancel = async (e) => {
        e.preventDefault();

        if (!bulkCancelReason.trim()) {
            alert('Please provide a cancellation reason');
            return;
        }

        setBulkActionLoading(true);
        const results = { success: [], failed: [] };
        const cashPaymentsToCancel = Array.from(selectedCashPayments);
        setBulkActionProgress({ current: 0, total: cashPaymentsToCancel.length });

        for (let i = 0; i < cashPaymentsToCancel.length; i++) {
            const cashPaymentId = cashPaymentsToCancel[i];
            const cashPayment = pendingCashPayments.find(c => c._id === cashPaymentId) || allCashPayments.find(c => c._id === cashPaymentId);

            try {
                setBulkActionProgress({ current: i + 1, total: cashPaymentsToCancel.length });

                const response = await axiosInstance.patch(
                    `/api/cashbook/${cashPaymentId}/cancel`,
                    { cancellationReason: bulkCancelReason.trim() }
                );

                if (response.data.success) {
                    results.success.push({
                        voucherNumber: cashPayment?.voucherNumber || 'Unknown',
                        supplierName: cashPayment?.supplierName || 'Unknown',
                        amount: cashPayment?.amount || 0
                    });
                }
            } catch (err) {
                results.failed.push({
                    voucherNumber: cashPayment?.voucherNumber || 'Unknown',
                    supplierName: cashPayment?.supplierName || 'Unknown',
                    amount: cashPayment?.amount || 0,
                    error: err.response?.data?.message || 'Failed to cancel'
                });
            }
        }

        setBulkActionResults(results);
        setBulkActionLoading(false);

        if (activeTab === 'pending') {
            fetchPending(pendingSearchTerm, pendingCompanyFilter, pendingSupplierFilter, pendingFromDate, pendingToDate);
        } else if (activeTab === 'all') {
            if (viewMode === 'list') {
                fetchAllCashPayments(currentPage, searchTerm, statusFilter, companyFilter, supplierFilter, fromDate, toDate);
            } else {
                fetchGroupedCashbook(groupByField);
            }
        }

        setSelectedCashPayments(new Set());

        const successCount = results.success.length;
        const failedCount = results.failed.length;
        alert(`✅ Cancelled: ${successCount}\n❌ Failed: ${failedCount}\n\nCheck the modal for details.`);
    };

    const handleBulkReissue = async (e) => {
        e.preventDefault();

        setBulkActionLoading(true);
        const cashPaymentsToReissue = Array.from(selectedCashPayments);
        setBulkActionProgress({ current: 0, total: cashPaymentsToReissue.length });

        try {
            const response = await axiosInstance.post('/api/cashbook/bulk-reissue', {
                cashPaymentIds: cashPaymentsToReissue
            });

            if (response.data.success) {
                setBulkActionResults(response.data.results);

                if (viewMode === 'list') {
                    fetchAllCashPayments(currentPage, searchTerm, statusFilter, companyFilter, supplierFilter, fromDate, toDate);
                } else {
                    fetchGroupedCashbook(groupByField);
                }

                setSelectedCashPayments(new Set());

                const successCount = response.data.results.success.length;
                const failedCount = response.data.results.failed.length;
                alert(`✅ Re-submitted: ${successCount}\n❌ Failed: ${failedCount}\n\nCheck the modal for details.`);
            }
        } catch (err) {
            alert('Bulk reissue failed: ' + (err.response?.data?.message || err.message));
            setBulkActionResults({ success: [], failed: [] });
        } finally {
            setBulkActionLoading(false);
        }
    };

    // ========== VIEW ALL FUNCTIONS ==========
    const fetchAllCashPayments = async (page = 1, search = '', status = '', company = '', supplier = '', fromDate = '', toDate = '') => {
        setLoadingAll(true);
        try {
            const response = await axiosInstance.get(
                `/api/cashbook?page=${page}&limit=10&search=${search}&status=${status}&company=${company}&supplier=${supplier}&fromDate=${fromDate}&toDate=${toDate}`
            );
            if (response.data.success) {
                setAllCashPayments(response.data.data);
                setTotalPages(Number(response.data.totalPages));
                setCurrentPage(Number(response.data.currentPage));
            }
        } catch (err) {
            console.error('Error fetching cash payments:', err);
        } finally {
            setLoadingAll(false);
        }
    };
    
    const fetchSuppliersList = async () => {
        try {
            const response = await axiosInstance.get('/api/cashbook/suppliers');
            if (response.data.success) {
                setSuppliersList(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching suppliers:', err);
        }
    };
    
    const fetchGroupedCashbook = async (groupBy = groupByField) => {
        setLoadingAll(true);
        try {
            const response = await axiosInstance.get(
                `/api/cashbook/grouped?groupBy=${groupBy}&status=${statusFilter}&company=${companyFilter}&supplier=${supplierFilter}&fromDate=${fromDate}&toDate=${toDate}`
            );
            if (response.data.success) {
                setGroupedData(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching grouped cashbook:', err);
        } finally {
            setLoadingAll(false);
        }
    };
    
    const toggleGroup = (groupId) => {
        const newExpanded = new Set(expandedGroups);
        if (newExpanded.has(groupId)) {
            newExpanded.delete(groupId);
        } else {
            newExpanded.add(groupId);
        }
        setExpandedGroups(newExpanded);
    };
    
    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
        if (viewMode === 'list') {
            fetchAllCashPayments(1, e.target.value, statusFilter, companyFilter, supplierFilter, fromDate, toDate);
        }
    };
    
    const handleStatusFilter = (status) => {
        setStatusFilter(status);
        setCurrentPage(1);
        if (viewMode === 'list') {
            fetchAllCashPayments(1, searchTerm, status, companyFilter, supplierFilter, fromDate, toDate);
        } else {
            fetchGroupedCashbook();
        }
    };
    
    const handleCompanyFilter = (company) => {
        setCompanyFilter(company);
        setCurrentPage(1);
        if (viewMode === 'list') {
            fetchAllCashPayments(1, searchTerm, statusFilter, company, supplierFilter, fromDate, toDate);
        } else {
            fetchGroupedCashbook();
        }
    };
    
    const handleSupplierFilter = (supplier) => {
        setSupplierFilter(supplier);
        setCurrentPage(1);
        if (viewMode === 'list') {
            fetchAllCashPayments(1, searchTerm, statusFilter, companyFilter, supplier, fromDate, toDate);
        } else {
            fetchGroupedCashbook();
        }
    };
    
    const handleViewModeChange = (mode) => {
        setViewMode(mode);
        if (mode === 'grouped') {
            fetchGroupedCashbook();
        } else {
            fetchAllCashPayments(currentPage, searchTerm, statusFilter, companyFilter, supplierFilter, fromDate, toDate);
        }
    };
    
    const handleGroupByChange = (field) => {
        setGroupByField(field);
        fetchGroupedCashbook(field);
    };

    // Load data when tab changes
    useEffect(() => {
        if (activeTab === 'pending') {
            fetchPending();
            fetchSuppliersList();
        }
        if (activeTab === 'all') {
            fetchSuppliersList();
            if (viewMode === 'list') {
                fetchAllCashPayments(currentPage, searchTerm, statusFilter, companyFilter, supplierFilter, fromDate, toDate);
            } else {
                fetchGroupedCashbook();
            }
        }
    }, [activeTab]);
    
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit', month: '2-digit', year: 'numeric'
        });
    };
    
    const formatAmount = (amount) => {
        return new Intl.NumberFormat('en-QA', {
            style: 'currency', currency: 'QAR', minimumFractionDigits: 2
        }).format(amount);
    };
    
    const getShortCompanyName = (fullName) => {
        const companyMap = {
            'BRADMA QATAR FOOD (BQF)': 'BQF',
            'AHLA MART TRADING': 'AMT',
            'BRADMA QATAR WLL': 'BQBM',
            'CENTRAL AUTO PARTS (CAP)': 'CAP'
        };
        return companyMap[fullName] || fullName;
    };

    
    return (
        <div className="cheque-register-container" style={{ backgroundImage: `url(${receptionBg})` }}>
            <div className="cheque-register-content">
                <div className="cheque-header">
                    <img src="/logo.png" alt="Logo" className="app-logo" />
                    <div className="header-text">
                        <h1>Cashbook</h1>
                        <p>2-Step Cash Payment Submission & Issuance System</p>
                    </div>
                    <div className="header-actions">
                        <div className="user-info">
                            <strong>{user?.fullName || user?.username}</strong>
                        </div>
                        <button onClick={() => navigate('/purpose-selection')} className="btn-back">
                            ← Back
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="cheque-tabs">
                    {/* Tab 1: Only for users with create permission */}
                    {canCreate && (
                        <button
                            className={`tab-button ${activeTab === 'issue' ? 'active' : ''}`}
                            onClick={() => { setActiveTab('issue'); setSelectedCashPayments(new Set()); }}
                        >
                            Submit Cash Payment
                        </button>
                    )}
                    {/* Tab 2: For system_receptionist and system_admin */}
                    {(user?.role === 'system_receptionist' || user?.role === 'system_admin') && (
                        <button
                            className={`tab-button ${activeTab === 'pending' ? 'active' : ''}`}
                            onClick={() => { setActiveTab('pending'); setSelectedCashPayments(new Set()); }}
                        >
                            Pending Issuance {pendingCashPayments.length > 0 && `(${pendingCashPayments.length})`}
                        </button>
                    )}
                    {/* Tab 3: All roles */}
                    <button
                        className={`tab-button ${activeTab === 'all' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('all'); setSelectedCashPayments(new Set()); }}
                    >
                        All Cash Payments
                    </button>
                </div>

                {/* TAB 1: SUBMIT CASH PAYMENT - Only for users with create permission */}
                {activeTab === 'issue' && canCreate && (
                    <div className="tab-content">
                        {issueSuccess && <div className="alert alert-success">{issueSuccess}</div>}
                        {issueError && <div className="alert alert-error">{issueError}</div>}
                        
                        {/* Bulk Upload Section */}
                        <div className="bulk-upload-section">
                            <h3>📊 Bulk Upload from Excel</h3>
                            <p className="bulk-upload-info">
                                Upload an Excel file with columns: <strong>Voucher Number, Destination Ledger Name, Amount</strong>
                                <br />
                                Optional columns: Destination Ledger Code, Source Ledger Code, Source Ledger Name
                                <br />
                                <strong>Note:</strong> Enter the voucher number exactly as in the system — do not add extra characters (. , - space) at the end. Returned vouchers can be uploaded again with the same number.
                            </p>
                            {bulkUploadSuccess && <div className="alert alert-success" style={{whiteSpace: 'pre-line'}}>{bulkUploadSuccess}</div>}
                            {bulkUploadError && <div className="alert alert-error" style={{whiteSpace: 'pre-line'}}>{bulkUploadError}</div>}
                            <div className="bulk-upload-controls">
                                <select
                                    value={bulkUploadCompany}
                                    onChange={(e) => setBulkUploadCompany(e.target.value)}
                                    disabled={bulkUploadLoading}
                                    className="company-select"
                                    style={{ marginRight: '10px', padding: '8px' }}
                                >
                                    <option value="">Select Company *</option>
                                    <option value="BRADMA QATAR FOOD (BQF)">BQF</option>
                                    <option value="AHLA MART TRADING">AMT</option>
                                    <option value="BRADMA QATAR WLL">BQBM</option>
                                    <option value="CENTRAL AUTO PARTS (CAP)">CAP</option>
                                </select>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    accept=".xls,.xlsx,.csv"
                                    onChange={handleFileSelect}
                                    disabled={bulkUploadLoading}
                                />
                                {bulkUploadFile && <span className="file-selected">✓ {bulkUploadFile.name}</span>}
                                <div className="bulk-upload-buttons">
                                    <button
                                        type="button"
                                        onClick={handleBulkUpload}
                                        disabled={!bulkUploadFile || !bulkUploadCompany || bulkUploadLoading}
                                        className="btn btn-primary"
                                    >
                                        {bulkUploadLoading ? 'Uploading...' : 'Upload Excel'}
                                    </button>
                                    {bulkUploadFile && !bulkUploadLoading && (
                                        <button
                                            type="button"
                                            onClick={clearBulkUpload}
                                            className="btn btn-secondary"
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>
                            </div>
                            {bulkCreated.length > 0 && (
                                <div style={{ marginTop: '16px', overflowX: 'auto' }}>
                                    <h4 style={{ margin: '0 0 8px' }}>📎 Attach documents to the uploaded entries (optional)</h4>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                                        <thead>
                                            <tr style={{ textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                                                <th style={{ padding: '6px' }}>Voucher</th>
                                                <th style={{ padding: '6px' }}>Supplier</th>
                                                <th style={{ padding: '6px' }}>Amount</th>
                                                <th style={{ padding: '6px' }}>Attachments</th>
                                                <th style={{ padding: '6px' }}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {bulkCreated.map(row => (
                                                <tr key={row._id} style={{ borderBottom: '1px solid #eee' }}>
                                                    <td style={{ padding: '6px' }}>{row.voucherNumber}</td>
                                                    <td style={{ padding: '6px' }}>{row.supplierName}</td>
                                                    <td style={{ padding: '6px' }}>{row.amount}</td>
                                                    <td style={{ padding: '6px' }}>{row.docCount > 0 ? `✓ ${row.docCount} file(s)` : '—'}</td>
                                                    <td style={{ padding: '6px' }}>
                                                        <label className="btn btn-secondary" style={{ cursor: 'pointer', margin: 0, padding: '4px 12px' }}>
                                                            {bulkAttachingId === row._id ? 'Uploading...' : 'Attach'}
                                                            <input type="file" multiple hidden
                                                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                                                                disabled={bulkAttachingId !== null}
                                                                onChange={(e) => handleBulkAttach(row._id, e)} />
                                                        </label>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    <button type="button" className="btn btn-secondary" style={{ marginTop: '8px' }}
                                        onClick={() => setBulkCreated([])} disabled={bulkAttachingId !== null}>
                                        Done
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="divider-text">OR</div>
                        
                        <form onSubmit={handleIssueCashPayment} className="cheque-form">
                            <h3>Manual Entry</h3>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>Voucher Number *</label>
                                    <input type="text" name="voucherNumber" value={issueForm.voucherNumber}
                                        onChange={handleIssueChange} required />
                                    {hasTrailingExtra(issueForm.voucherNumber) && (
                                        <small style={{ color: '#c62828' }}>Remove the extra character at the end (. , - etc.)</small>
                                    )}
                                </div>
                                <div className="form-group autocomplete-wrapper">
                                    <label>Supplier *</label>
                                    <input type="text" name="supplierName" value={issueForm.supplierName}
                                        onChange={handleIssueChange} autoComplete="off" required />
                                    {showSupplierSuggestions && supplierSuggestions.length > 0 && (
                                        <div className="autocomplete-dropdown">
                                            {supplierSuggestions.map((suggestion, index) => (
                                                <div key={index} className="autocomplete-item"
                                                    onClick={() => handleSelectSupplier(suggestion)}>
                                                    <div className="suggestion-company">{suggestion.supplierName}</div>
                                                    <div className="suggestion-details">
                                                        Last used: {new Date(suggestion.lastUsed).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {loadingSupplierSuggestions && (
                                        <div className="autocomplete-loading">Searching...</div>
                                    )}
                                </div>
                                <div className="form-group">
                                    <label>Company *</label>
                                    <select name="company" value={issueForm.company}
                                        onChange={handleIssueChange} required>
                                        <option value="">Select Company</option>
                                        <option value="BRADMA QATAR FOOD (BQF)">BQF</option>
                                        <option value="AHLA MART TRADING">AMT</option>
                                        <option value="BRADMA QATAR WLL">BQBM</option>
                                        <option value="CENTRAL AUTO PARTS (CAP)">CAP</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Amount (QAR) *</label>
                                    <input type="number" name="amount" value={issueForm.amount}
                                        onChange={handleIssueChange} step="0.01" min="0" required />
                                </div>
                                <div className="form-group">
                                    <label>Attachments (optional)</label>
                                    <input type="file" ref={issueFilesRef} multiple
                                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                                        onChange={(e) => setIssueFiles(Array.from(e.target.files))} />
                                    {issueFiles.length > 0 && <small>{issueFiles.length} file(s) selected</small>}
                                </div>
                            </div>
                            
                            <div className="digital-signature-info">
                                <p>✍️ Your digital signature will be automatically generated upon submission</p>
                                <p className="signature-preview">Signature: Digitally signed by {user?.fullName || user?.username}</p>
                            </div>
                            
                            <div className="form-actions">
                                <button type="submit" className="btn btn-primary" disabled={issueLoading}>
                                    {issueLoading ? 'Submitting...' : 'Submit Cash Payment'}
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* TAB 2: PENDING ISSUANCE - For users with cashbook access */}
                {activeTab === 'pending' && hasCashbookAccess && (
                    <div className="tab-content">
                        {/* Filters for Pending */}
                        <div className="search-filter-bar">
                            <input 
                                type="text" 
                                placeholder="Search voucher, supplier..." 
                                value={pendingSearchTerm}
                                onChange={handlePendingSearch} 
                                className="search-input" 
                            />
                            
                            <select 
                                value={pendingCompanyFilter}
                                onChange={(e) => handlePendingCompanyFilter(e.target.value)}
                                className="filter-select"
                            >
                                <option value="">All Companies</option>
                                <option value="BRADMA QATAR FOOD (BQF)">BQF</option>
                                <option value="AHLA MART TRADING">AMT</option>
                                <option value="BRADMA QATAR WLL">BQBM</option>
                                <option value="CENTRAL AUTO PARTS (CAP)">CAP</option>
                            </select>
                            
                            <select 
                                value={pendingSupplierFilter}
                                onChange={(e) => handlePendingSupplierFilter(e.target.value)}
                                className="filter-select"
                            >
                                <option value="">All Suppliers</option>
                                {suppliersList.map((supplier, idx) => (
                                    <option key={idx} value={supplier}>{supplier}</option>
                                ))}
                            </select>
                            
                            <div className="date-filters">
                                <input 
                                    type="date" 
                                    value={pendingFromDate}
                                    onChange={(e) => { 
                                        setPendingFromDate(e.target.value); 
                                        fetchPending(pendingSearchTerm, pendingCompanyFilter, pendingSupplierFilter, e.target.value, pendingToDate);
                                    }}
                                    className="date-input" 
                                    placeholder="From Date" 
                                />
                                <input 
                                    type="date" 
                                    value={pendingToDate}
                                    onChange={(e) => { 
                                        setPendingToDate(e.target.value); 
                                        fetchPending(pendingSearchTerm, pendingCompanyFilter, pendingSupplierFilter, pendingFromDate, e.target.value);
                                    }}
                                    className="date-input" 
                                    placeholder="To Date" 
                                />
                            </div>
                        </div>
                        
                        {/* Bulk Actions */}
                        {selectedCashPayments.size > 0 && (
                            <div className="bulk-actions-bar">
                                <span className="selected-count">{selectedCashPayments.size} selected</span>
                                <button onClick={openBulkCollectModal} className="btn btn-primary">
                                    Bulk Issue to Suppliers
                                </button>
                                <button onClick={openBulkReturnModal} className="btn btn-warning">
                                    Bulk Return
                                </button>
                                <button onClick={openBulkCancelModal} className="btn btn-danger">
                                    ❌ Cancel Selected ({selectedCashPayments.size})
                                </button>
                                <button onClick={() => setSelectedCashPayments(new Set())} className="btn btn-secondary">
                                    Clear Selection
                                </button>
                            </div>
                        )}
                        
                        {loadingPending ? (
                            <div className="loading-state">Loading pending cash payments...</div>
                        ) : pendingCashPayments.length === 0 ? (
                            <div className="empty-state">
                                <p>No cash payments pending issuance</p>
                            </div>
                        ) : (
                            <div className="cheques-table-container" ref={pendingTableRef}>
                                <table className="cheques-table">
                                    <thead>
                                        <tr>
                                            <th className="th-actions-col">
                                                <div className="cell-select-actions">
                                                    <input 
                                                        type="checkbox"
                                                        checked={selectedCashPayments.size === pendingCashPayments.length && pendingCashPayments.length > 0}
                                                        onChange={toggleSelectAll}
                                                        className="row-checkbox"
                                                        title="Select all"
                                                    />
                                                    <span className="th-actions-label">Select / Actions</span>
                                                </div>
                                            </th>
                                            <th>Voucher No.</th>
                                            <th>Supplier</th>
                                            <th>Company</th>
                                            <th>Amount</th>
                                            <th>Submitted By</th>
                                            <th>Submitted Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pendingCashPayments.map((payment) => (
                                            <tr key={payment._id}>
                                                <td className="td-actions-col">
                                                    <div className="cell-select-actions">
                                                        <input 
                                                            type="checkbox"
                                                            checked={selectedCashPayments.has(payment._id)}
                                                            onChange={() => toggleCashPaymentSelection(payment._id)}
                                                            className="row-checkbox"
                                                            title="Select row"
                                                        />
                                                        <div className="action-buttons-vertical">
                                                            <button
                                                                type="button"
                                                                className="btn-collect btn-icon-only"
                                                                onClick={() => openCollectModal(payment)}
                                                                disabled={collectCheckingId === payment._id}
                                                                title="Issue to Supplier"
                                                            >
                                                                <FiSend size={18} aria-hidden />
                                                            </button>
                                                            <button 
                                                                type="button"
                                                                className="btn-return btn-icon-only"
                                                                onClick={() => openReturnModal(payment)}
                                                                title="Return"
                                                            >
                                                                <FiArrowLeft size={18} aria-hidden />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>{payment.voucherNumber}</td>
                                                <td>{payment.supplierName}</td>
                                                <td>{getShortCompanyName(payment.company)}</td>
                                                <td className="amount-cell">{formatAmount(payment.amount)}</td>
                                                <td>{payment.issuedBy}</td>
                                                <td>{formatDate(payment.issuedAt)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}


                {/* TAB 3: ALL CASH PAYMENTS */}
                {activeTab === 'all' && (
                    <div className="tab-content">
                        <div className="search-filter-bar enhanced">
                            <input type="text" placeholder="Search cash payments..." value={searchTerm}
                                onChange={handleSearch} className="search-input" />
                            
                            <select 
                                value={statusFilter}
                                onChange={(e) => handleStatusFilter(e.target.value)}
                                className="filter-select"
                            >
                                <option value="">All Status</option>
                                <option value="pending">Pending</option>
                                <option value="collected">Issued to Supplier</option>
                                <option value="returned">Returned</option>
                                <option value="cancelled">Cancelled</option>
                            </select>
                            
                            <select 
                                value={companyFilter}
                                onChange={(e) => handleCompanyFilter(e.target.value)}
                                className="filter-select"
                            >
                                <option value="">All Companies</option>
                                <option value="BRADMA QATAR FOOD (BQF)">BQF</option>
                                <option value="AHLA MART TRADING">AMT</option>
                                <option value="BRADMA QATAR WLL">BQBM</option>
                                <option value="CENTRAL AUTO PARTS (CAP)">CAP</option>
                            </select>
                            
                            <select 
                                value={supplierFilter}
                                onChange={(e) => handleSupplierFilter(e.target.value)}
                                className="filter-select"
                            >
                                <option value="">All Suppliers</option>
                                {suppliersList.map((supplier, idx) => (
                                    <option key={idx} value={supplier}>{supplier}</option>
                                ))}
                            </select>
                            
                            <div className="date-filters">
                                <input type="date" value={fromDate}
                                    onChange={(e) => { 
                                        setFromDate(e.target.value); 
                                        if (viewMode === 'list') {
                                            fetchAllCashPayments(1, searchTerm, statusFilter, companyFilter, supplierFilter, e.target.value, toDate);
                                        } else {
                                            fetchGroupedCashbook();
                                        }
                                    }}
                                    className="date-input" 
                                    placeholder="From Date" />
                                <input type="date" value={toDate}
                                    onChange={(e) => { 
                                        setToDate(e.target.value); 
                                        if (viewMode === 'list') {
                                            fetchAllCashPayments(1, searchTerm, statusFilter, companyFilter, supplierFilter, fromDate, e.target.value);
                                        } else {
                                            fetchGroupedCashbook();
                                        }
                                    }}
                                    className="date-input" 
                                    placeholder="To Date" />
                            </div>
                            
                            <div className="view-mode-toggle">
                                <button 
                                    className={`btn-view-mode ${viewMode === 'list' ? 'active' : ''}`}
                                    onClick={() => handleViewModeChange('list')}
                                    title="List View"
                                >
                                    📋 List
                                </button>
                                <button 
                                    className={`btn-view-mode ${viewMode === 'grouped' ? 'active' : ''}`}
                                    onClick={() => handleViewModeChange('grouped')}
                                    title="Grouped View"
                                >
                                    📊 Group
                                </button>
                            </div>
                        </div>

                        {viewMode === 'grouped' && (
                            <div className="group-by-options">
                                <label>Group By:</label>
                                <select 
                                    value={groupByField}
                                    onChange={(e) => handleGroupByChange(e.target.value)}
                                    className="group-by-select"
                                >
                                    <option value="company">Company</option>
                                    <option value="supplier">Supplier</option>
                                    <option value="status">Status</option>
                                </select>
                            </div>
                        )}
                        
                        {loadingAll ? (
                            <div className="loading-state">Loading cash payments...</div>
                        ) : viewMode === 'list' ? (
                            allCashPayments.length === 0 ? (
                                <div className="empty-state"><p>No cash payments found</p></div>
                            ) : (
                                <>
                                    {/* Bulk Action Buttons for All Cash Payments */}
                                    {selectedCashPayments.size > 0 && (() => {
                                        const selectedArray = Array.from(selectedCashPayments);
                                        const hasResubmittable = selectedArray.some(id => {
                                            const p = allCashPayments.find(c => c._id === id);
                                            return p && (p.status === 'returned' || p.status === 'cancelled');
                                        });
                                        const hasCancellable = selectedArray.some(id => {
                                            const p = allCashPayments.find(c => c._id === id);
                                            return p && ['pending', 'returned', 'collected'].includes(p.status);
                                        });
                                        return (
                                            <div className="bulk-actions-bar">
                                                <div className="bulk-selection-info">
                                                    <span>{selectedCashPayments.size} of {allCashPayments.length} selected</span>
                                                </div>
                                                <div className="bulk-action-buttons">
                                                    {hasResubmittable && (
                                                        <button
                                                            className="btn btn-success btn-sm"
                                                            onClick={openBulkReissueModal}
                                                        >
                                                            🔄 Resubmit Selected ({selectedCashPayments.size})
                                                        </button>
                                                    )}
                                                    {hasCancellable && (
                                                        <button
                                                            className="btn btn-danger btn-sm"
                                                            onClick={openBulkCancelModal}
                                                            style={{ marginLeft: hasResubmittable ? '10px' : '0' }}
                                                        >
                                                            ❌ Cancel Selected ({selectedCashPayments.size})
                                                        </button>
                                                    )}
                                                    <button
                                                        className="btn btn-secondary btn-sm"
                                                        onClick={() => setSelectedCashPayments(new Set())}
                                                        style={{ marginLeft: (hasResubmittable || hasCancellable) ? '10px' : '0' }}
                                                    >
                                                        Clear Selection
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    <div className="cheques-table-container" ref={allCashbookTableRef}>
                                        <table className="cheques-table">
                                            <thead>
                                                <tr>
                                                    <th className="th-checkbox-col">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedCashPayments.size === allCashPayments.length && allCashPayments.length > 0}
                                                            onChange={() => {
                                                                if (selectedCashPayments.size === allCashPayments.length) {
                                                                    setSelectedCashPayments(new Set());
                                                                } else {
                                                                    setSelectedCashPayments(new Set(allCashPayments.map(c => c._id)));
                                                                }
                                                            }}
                                                            className="row-checkbox"
                                                            title="Select all"
                                                        />
                                                    </th>
                                                    <th className="th-view-col" title="View details" aria-label="View"></th>
                                                    <th>Voucher No.</th>
                                                    <th>Supplier</th>
                                                    <th>Company</th>
                                                    <th>Amount</th>
                                                    <th>Status</th>
                                                    <th>Submitted By</th>
                                                    <th>Issued By (Reception)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {allCashPayments.map((payment) => (
                                                    <tr key={payment._id}>
                                                        <td className="td-checkbox-col">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedCashPayments.has(payment._id)}
                                                                onChange={() => toggleCashPaymentSelection(payment._id)}
                                                                className="row-checkbox"
                                                                title="Select row"
                                                            />
                                                        </td>
                                                        <td className="td-view-col">
                                                            <div className="action-buttons-group">
                                                                <button
                                                                    type="button"
                                                                    className="btn-view-details btn-view-icon"
                                                                    onClick={() => { setMissingAttachments(new Set()); setViewModal(payment); }}
                                                                    title="View details"
                                                                    aria-label="View details"
                                                                >
                                                                    <FiEye size={18} aria-hidden />
                                                                </button>
                                                                {['pending', 'returned', 'collected'].includes(payment.status) && canApprove && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn-cancel-icon"
                                                                        onClick={() => {
                                                                            setSelectedCashPayment(payment);
                                                                            openCancelModal(payment);
                                                                        }}
                                                                        title="Cancel Payment"
                                                                        aria-label="Cancel Payment"
                                                                    >
                                                                        <FiXCircle size={18} aria-hidden />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>{payment.voucherNumber}</td>
                                                        <td>{payment.supplierName}</td>
                                                        <td>{getShortCompanyName(payment.company)}</td>
                                                        <td className="amount-cell">{formatAmount(payment.amount)}</td>
                                                        <td>
                                                            <span className={`status-badge status-${payment.status}`}>
                                                                {payment.status === 'pending' ? '🟡 Pending Issuance' : 
                                                                 payment.status === 'collected' ? '🟢 Issued to Supplier' : 
                                                                 payment.status === 'returned' ? '🔴 Returned' :
                                                                 '⚫ Cancelled'}
                                                            </span>
                                                            {payment.complaints && <span className="complaint-indicator" title={payment.complaints}>⚠️</span>}
                                                        </td>
                                                        <td>{payment.issuedBy}</td>
                                                        <td>{payment.collectedBy || '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    {totalPages > 1 && (
                                        <div className="pagination">
                                            <button onClick={() => {
                                                const newPage = currentPage - 1;
                                                setCurrentPage(newPage);
                                                fetchAllCashPayments(newPage, searchTerm, statusFilter, companyFilter, supplierFilter, fromDate, toDate);
                                            }}
                                                disabled={currentPage === 1} className="pagination-btn">Previous</button>
                                            <span className="pagination-info">Page {currentPage} of {totalPages}</span>
                                            <button onClick={() => {
                                                const newPage = currentPage + 1;
                                                setCurrentPage(newPage);
                                                fetchAllCashPayments(newPage, searchTerm, statusFilter, companyFilter, supplierFilter, fromDate, toDate);
                                            }}
                                                disabled={currentPage === totalPages} className="pagination-btn">Next</button>
                                        </div>
                                    )}
                                </>
                            )
                        ) : (
                            groupedData.length === 0 ? (
                                <div className="empty-state"><p>No cash payments found</p></div>
                            ) : (
                                <div className="grouped-view-container">
                                    {groupedData.map((group, idx) => (
                                        <div key={idx} className="group-card">
                                            <div 
                                                className="group-header"
                                                onClick={() => toggleGroup(group._id)}
                                            >
                                                <div className="group-title">
                                                    <span className="expand-icon">
                                                        {expandedGroups.has(group._id) ? '▼' : '▶'}
                                                    </span>
                                                    <h3>{group._id || 'Unknown'}</h3>
                                                </div>
                                                <div className="group-stats">
                                                    <div className="stat-item">
                                                        <span className="stat-label">Total:</span>
                                                        <span className="stat-value">{group.totalCount} payments</span>
                                                    </div>
                                                    <div className="stat-item">
                                                        <span className="stat-label">Amount:</span>
                                                        <span className="stat-value">{formatAmount(group.totalAmount)}</span>
                                                    </div>
                                                    <div className="stat-breakdown">
                                                        {group.pendingCount > 0 && (
                                                            <span className="badge badge-pending">
                                                                {group.pendingCount} Pending ({formatAmount(group.pendingAmount)})
                                                            </span>
                                                        )}
                                                        {group.collectedCount > 0 && (
                                                            <span className="badge badge-collected">
                                                                {group.collectedCount} Collected ({formatAmount(group.collectedAmount)})
                                                            </span>
                                                        )}
                                                        {group.returnedCount > 0 && (
                                                            <span className="badge badge-returned">
                                                                {group.returnedCount} Returned ({formatAmount(group.returnedAmount)})
                                                            </span>
                                                        )}
                                                        {group.cancelledCount > 0 && (
                                                            <span className="badge badge-cancelled">
                                                                {group.cancelledCount} Cancelled
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {expandedGroups.has(group._id) && (
                                                <div className="group-details">
                                                    <p className="group-note">Click on a row to view details</p>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )
                        )}
                    </div>
                )}


                {/* COLLECTION MODAL */}
                {showCollectModal && selectedCashPayment && (
                    <div className="modal-overlay" onClick={closeCollectModal}>
                        <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>Issue Cash Payment to Supplier</h2>
                                <button className="modal-close" onClick={closeCollectModal}>×</button>
                            </div>
                            <div className="modal-body">
                                {collectError && <div className="alert alert-error">{collectError}</div>}
                                
                                <div className="cheque-details-section">
                                    <h3>Cash Payment Details</h3>
                                    <div className="detail-grid">
                                        <div className="detail-item">
                                            <label>Voucher Number:</label>
                                            <span>{selectedCashPayment.voucherNumber}</span>
                                        </div>
                                        <div className="detail-item">
                                            <label>Supplier:</label>
                                            <span>{selectedCashPayment.supplierName}</span>
                                        </div>
                                        <div className="detail-item">
                                            <label>Company:</label>
                                            <span>{getShortCompanyName(selectedCashPayment.company)}</span>
                                        </div>
                                        <div className="detail-item">
                                            <label>Amount:</label>
                                            <span className="amount-highlight">{formatAmount(selectedCashPayment.amount)}</span>
                                        </div>
                                    </div>
                                </div>
                                
                                <form onSubmit={handleCollectCashPayment} className="collection-form">
                                    <h3>Issuance Details</h3>
                                    <div className="form-grid">
                                        <div className="form-group">
                                            <label>Receipt Number *</label>
                                            <input type="text" name="receiptNumber" value={collectForm.receiptNumber}
                                                onChange={handleCollectChange} required />
                                        </div>
                                        <div className="form-group autocomplete-wrapper">
                                            <label>Receiver Name *</label>
                                            <input type="text" name="receiverName" value={collectForm.receiverName}
                                                onChange={handleCollectChange} autoComplete="off" required />
                                            {showReceiverSuggestions && receiverSuggestions.length > 0 && (
                                                <div className="autocomplete-dropdown">
                                                    {receiverSuggestions.map((suggestion, index) => (
                                                        <div key={index} className="autocomplete-item"
                                                            onClick={() => handleSelectReceiver(suggestion)}>
                                                            <div className="suggestion-company">{suggestion.receiverName}</div>
                                                            <div className="suggestion-details">
                                                                {suggestion.contactNumber} • {suggestion.supplierName}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            {loadingReceiverSuggestions && (
                                                <div className="autocomplete-loading">Searching...</div>
                                            )}
                                        </div>
                                        <div className="form-group">
                                            <label>Contact Number *</label>
                                            <input type="tel" name="contactNumber" value={collectForm.contactNumber}
                                                onChange={handleCollectChange} placeholder="33445566" maxLength="8" required />
                                        </div>
                                        <div className="form-group">
                                            <label>Complaints (Optional)</label>
                                            <input type="text" name="complaints" value={collectForm.complaints}
                                                onChange={handleCollectChange} />
                                        </div>
                                    </div>

                                    <div className="signatures-section">
                                        <div className="signature-group">
                                            <SignaturePadModal
                                                label="Receiver Signature"
                                                required
                                                value={receiverSig}
                                                onChange={setReceiverSig}
                                            />
                                        </div>

                                        <div className="signature-group">
                                            <label>Receptionist Signature</label>
                                            <div className="digital-signature-display">
                                                <p>✍️ Digitally signed by {user?.fullName || user?.username}</p>
                                                <p className="signature-note">(Auto-generated upon submission)</p>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="form-actions">
                                        <button type="button" onClick={closeCollectModal} className="btn btn-secondary">
                                            Cancel
                                        </button>
                                        <button type="submit" className="btn btn-primary" disabled={collectLoading}>
                                            {collectLoading ? 'Processing...' : 'Issue to Supplier'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {/* BULK ISSUE TO SUPPLIERS MODAL */}
                {showBulkCollectModal && (
                    <div className="modal-overlay" onClick={closeBulkCollectModal}>
                        <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>Bulk Issue Cash Payments to Suppliers ({selectedCashPayments.size} selected)</h2>
                                <button className="modal-close" onClick={closeBulkCollectModal}>×</button>
                            </div>
                            <div className="modal-body">
                                {bulkActionLoading && (
                                    <div className="progress-bar">
                                        <div className="progress-fill" style={{ width: `${(bulkActionProgress.current / bulkActionProgress.total) * 100}%` }}></div>
                                        <span className="progress-text">Processing {bulkActionProgress.current} of {bulkActionProgress.total}...</span>
                                    </div>
                                )}
                                
                                <form onSubmit={handleBulkCollect} className="collection-form">
                                    <h3>Issuance Details (Same for all selected)</h3>
                                    <div className="form-grid">
                                        <div className="form-group">
                                            <label>Receipt Number *</label>
                                            <input type="text" value={bulkCollectForm.receiptNumber}
                                                onChange={(e) => setBulkCollectForm({...bulkCollectForm, receiptNumber: e.target.value})} required />
                                        </div>
                                        <div className="form-group autocomplete-wrapper">
                                            <label>Receiver Name *</label>
                                            <input type="text" name="receiverName" value={bulkCollectForm.receiverName}
                                                onChange={handleBulkCollectChange} autoComplete="off" required />
                                            {showBulkReceiverSuggestions && bulkReceiverSuggestions.length > 0 && (
                                                <div className="autocomplete-dropdown">
                                                    {bulkReceiverSuggestions.map((suggestion, index) => (
                                                        <div key={index} className="autocomplete-item"
                                                            onClick={() => handleSelectBulkReceiver(suggestion)}>
                                                            <div className="suggestion-company">{suggestion.receiverName}</div>
                                                            <div className="suggestion-details">
                                                                {suggestion.contactNumber} • {suggestion.supplierName}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            {loadingBulkReceiverSuggestions && (
                                                <div className="autocomplete-loading">Searching...</div>
                                            )}
                                        </div>
                                        <div className="form-group">
                                            <label>Contact Number *</label>
                                            <input type="tel" value={bulkCollectForm.contactNumber}
                                                onChange={(e) => setBulkCollectForm({...bulkCollectForm, contactNumber: e.target.value})}
                                                placeholder="33445566" maxLength="8" required />
                                        </div>
                                        <div className="form-group">
                                            <label>Complaints (Optional)</label>
                                            <input type="text" value={bulkCollectForm.complaints}
                                                onChange={(e) => setBulkCollectForm({...bulkCollectForm, complaints: e.target.value})} />
                                        </div>
                                    </div>

                                    <div className="signatures-section">
                                        <div className="signature-group">
                                            <SignaturePadModal
                                                label="Receiver Signature"
                                                required
                                                value={bulkReceiverSig}
                                                onChange={setBulkReceiverSig}
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="form-actions">
                                        <button type="button" onClick={closeBulkCollectModal} className="btn btn-secondary" disabled={bulkActionLoading}>
                                            Cancel
                                        </button>
                                        <button type="submit" className="btn btn-primary" disabled={bulkActionLoading}>
                                            {bulkActionLoading ? 'Processing...' : `Collect ${selectedCashPayments.size} Payments`}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {/* BULK RETURN MODAL */}
                {showBulkReturnModal && (
                    <div className="modal-overlay" onClick={closeBulkReturnModal}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>Bulk Return Cash Payments ({selectedCashPayments.size} selected)</h2>
                                <button className="modal-close" onClick={closeBulkReturnModal}>×</button>
                            </div>
                            <div className="modal-body">
                                {bulkActionLoading && (
                                    <div className="progress-bar">
                                        <div className="progress-fill" style={{ width: `${(bulkActionProgress.current / bulkActionProgress.total) * 100}%` }}></div>
                                        <span className="progress-text">Processing {bulkActionProgress.current} of {bulkActionProgress.total}...</span>
                                    </div>
                                )}
                                
                                <form onSubmit={handleBulkReturn} className="return-form">
                                    <div className="form-group">
                                        <label>Return Reason (Optional)</label>
                                        <textarea
                                            value={bulkReturnReason}
                                            onChange={(e) => setBulkReturnReason(e.target.value)}
                                            placeholder="Optional: Same reason will apply to all selected cash payments"
                                            rows="4"
                                            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                                        />
                                    </div>
                                    
                                    <div className="form-actions">
                                        <button type="button" onClick={closeBulkReturnModal} className="btn btn-secondary" disabled={bulkActionLoading}>
                                            Cancel
                                        </button>
                                        <button type="submit" className="btn btn-warning" disabled={bulkActionLoading}>
                                            {bulkActionLoading ? 'Processing...' : `Return ${selectedCashPayments.size} Payments`}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}


                {/* BULK REISSUE MODAL */}
                {showBulkReissueModal && (
                    <div className="modal-overlay" onClick={closeBulkReissueModal}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>🔄 Bulk Resubmit {selectedCashPayments.size} Cash Payments</h2>
                                <button className="modal-close" onClick={closeBulkReissueModal}>×</button>
                            </div>
                            <div className="modal-body">
                                {bulkActionLoading && (
                                    <div className="alert alert-info">
                                        Processing bulk resubmit...
                                    </div>
                                )}

                                {bulkActionResults.success.length > 0 || bulkActionResults.failed.length > 0 ? (
                                    <div className="bulk-results">
                                        {bulkActionResults.success.length > 0 && (
                                            <div className="alert alert-success">
                                                <strong>✅ Successfully Re-submitted ({bulkActionResults.success.length}):</strong>
                                                <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
                                                    {bulkActionResults.success.map((item, idx) => (
                                                        <li key={idx}>{item.voucherNumber} - {item.supplierName} - {formatAmount(item.amount)}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        {bulkActionResults.failed.length > 0 && (
                                            <div className="alert alert-error">
                                                <strong>❌ Failed ({bulkActionResults.failed.length}):</strong>
                                                <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
                                                    {bulkActionResults.failed.map((item, idx) => (
                                                        <li key={idx}>{item.voucherNumber} - {item.supplierName || 'Unknown'} - {item.error}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        <div className="form-actions">
                                            <button onClick={closeBulkReissueModal} className="btn btn-primary">Close</button>
                                        </div>
                                    </div>
                                ) : (
                                    <form onSubmit={handleBulkReissue}>
                                        <div className="alert alert-info">
                                            <p><strong>⚠️ Important:</strong></p>
                                            <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
                                                <li>Only returned or cancelled payments can be re-submitted</li>
                                                <li>Payments will be set back to "Pending Issuance" status</li>
                                                <li>Previous return/cancellation data will be cleared</li>
                                                <li>This action will be logged in the system</li>
                                            </ul>
                                        </div>
                                        <div className="form-group">
                                            <p><strong>Selected Payments: {selectedCashPayments.size}</strong></p>
                                            <p style={{ color: '#666', fontSize: '14px' }}>
                                                Click "Resubmit All" to process all selected payments.
                                            </p>
                                        </div>
                                        <div className="form-actions">
                                            <button type="button" onClick={closeBulkReissueModal} className="btn btn-secondary">Cancel</button>
                                            <button type="submit" className="btn btn-success" disabled={bulkActionLoading}>
                                                {bulkActionLoading ? 'Processing...' : `Resubmit All ${selectedCashPayments.size} Payments`}
                                            </button>
                                        </div>
                                    </form>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* BULK CANCEL MODAL */}
                {showBulkCancelModal && (
                    <div className="modal-overlay" onClick={closeBulkCancelModal}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>❌ Bulk Cancel {selectedCashPayments.size} Cash Payments</h2>
                                <button className="modal-close" onClick={closeBulkCancelModal}>×</button>
                            </div>
                            <div className="modal-body">
                                {bulkActionLoading && (
                                    <div className="progress-bar">
                                        <div className="progress-fill" style={{ width: `${(bulkActionProgress.current / bulkActionProgress.total) * 100}%` }}></div>
                                        <span className="progress-text">Processing {bulkActionProgress.current} of {bulkActionProgress.total}...</span>
                                    </div>
                                )}

                                {bulkActionResults.success.length > 0 || bulkActionResults.failed.length > 0 ? (
                                    <div className="bulk-results">
                                        {bulkActionResults.success.length > 0 && (
                                            <div className="alert alert-success">
                                                <strong>✅ Successfully Cancelled ({bulkActionResults.success.length}):</strong>
                                                <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
                                                    {bulkActionResults.success.map((item, idx) => (
                                                        <li key={idx}>{item.voucherNumber} - {item.supplierName} - {formatAmount(item.amount)}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        {bulkActionResults.failed.length > 0 && (
                                            <div className="alert alert-error">
                                                <strong>❌ Failed ({bulkActionResults.failed.length}):</strong>
                                                <ul style={{ marginTop: '10px', paddingLeft: '20px' }}>
                                                    {bulkActionResults.failed.map((item, idx) => (
                                                        <li key={idx}>{item.voucherNumber} - {item.supplierName} - {item.error}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        <div className="form-actions">
                                            <button onClick={closeBulkCancelModal} className="btn btn-primary">Close</button>
                                        </div>
                                    </div>
                                ) : (
                                    <form onSubmit={handleBulkCancel}>
                                        <div className="alert alert-warning">
                                            <p><strong>⚠️ Warning:</strong></p>
                                            <p>You are about to cancel {selectedCashPayments.size} cash payment(s). They will be marked as cancelled and cannot be issued to suppliers.</p>
                                        </div>
                                        <div className="form-group">
                                            <label>Cancellation Reason *</label>
                                            <textarea
                                                value={bulkCancelReason}
                                                onChange={(e) => setBulkCancelReason(e.target.value)}
                                                placeholder="Same reason will apply to all selected cash payments"
                                                rows="4"
                                                required
                                                style={{ width: '100%', padding: '10px', fontSize: '14px' }}
                                            />
                                        </div>
                                        <div className="form-actions">
                                            <button type="button" onClick={closeBulkCancelModal} className="btn btn-secondary">Cancel</button>
                                            <button type="submit" className="btn btn-danger" disabled={bulkActionLoading}>
                                                {bulkActionLoading ? `Cancelling ${bulkActionProgress.current}/${bulkActionProgress.total}...` : `Cancel ${selectedCashPayments.size} Payments`}
                                            </button>
                                        </div>
                                    </form>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* VIEW DETAILS MODAL */}
                {viewModal && (
                    <div className="modal-overlay" onClick={() => setViewModal(null)}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>Cash Payment Details</h2>
                                <button className="modal-close" onClick={() => setViewModal(null)}>×</button>
                            </div>
                            <div className="modal-body">
                                <div className="detail-grid">
                                    <div className="detail-item">
                                        <label>Voucher Number:</label>
                                        <span>{viewModal.voucherNumber}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Supplier:</label>
                                        <span>{viewModal.supplierName}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Company:</label>
                                        <span>{getShortCompanyName(viewModal.company)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Amount:</label>
                                        <span className="amount-highlight">{formatAmount(viewModal.amount)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Status:</label>
                                        <span className={`status-badge status-${viewModal.status}`}>
                                            {viewModal.status === 'pending' ? '🟡 Pending Issuance' : 
                                             viewModal.status === 'collected' ? '🟢 Issued to Supplier' : 
                                             viewModal.status === 'returned' ? '🔴 Returned' :
                                             '⚫ Cancelled'}
                                        </span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Submitted By (Accounts):</label>
                                        <span>{viewModal.issuedBy}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Issued At:</label>
                                        <span>{formatDate(viewModal.issuedAt)}</span>
                                    </div>
                                    {viewModal.status === 'returned' && (
                                        <>
                                            <div className="detail-item">
                                                <label>Returned By:</label>
                                                <span>{viewModal.returnedBy}</span>
                                            </div>
                                            <div className="detail-item">
                                                <label>Returned At:</label>
                                                <span>{formatDate(viewModal.returnedAt)}</span>
                                            </div>
                                            <div className="detail-item detail-complaints">
                                                <label>Return Reason:</label>
                                                <span className="complaints-text">{viewModal.returnReason}</span>
                                            </div>
                                        </>
                                    )}
                                    {viewModal.status === 'cancelled' && (
                                        <>
                                            <div className="detail-item">
                                                <label>Cancelled By:</label>
                                                <span>{viewModal.cancelledBy}</span>
                                            </div>
                                            <div className="detail-item">
                                                <label>Cancelled At:</label>
                                                <span>{formatDate(viewModal.cancelledAt)}</span>
                                            </div>
                                            <div className="detail-item detail-complaints">
                                                <label>Cancellation Reason:</label>
                                                <span className="complaints-text">{viewModal.cancellationReason}</span>
                                            </div>
                                        </>
                                    )}
                                    {viewModal.status === 'collected' && (
                                        <>
                                            <div className="detail-item">
                                                <label>Receipt Number:</label>
                                                <span>{viewModal.receiptNumber}</span>
                                            </div>
                                            <div className="detail-item">
                                                <label>Receiver Name:</label>
                                                <span>{viewModal.receiverName}</span>
                                            </div>
                                            <div className="detail-item">
                                                <label>Contact:</label>
                                                <span>{viewModal.contactNumber}</span>
                                            </div>
                                            <div className="detail-item">
                                                <label>Issued By (Reception):</label>
                                                <span>{viewModal.collectedBy}</span>
                                            </div>
                                            <div className="detail-item">
                                                <label>Collected At:</label>
                                                <span>{formatDate(viewModal.collectedAt)}</span>
                                            </div>
                                            {viewModal.complaints && (
                                                <div className="detail-item detail-complaints">
                                                    <label>Complaints:</label>
                                                    <span className="complaints-text">{viewModal.complaints}</span>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                                
                                {/* Signatures Section */}
                                <div className="signatures-display-section">
                                    <h3>Signatures</h3>
                                    <div className="signatures-grid">
                                        {/* Accountant Signature */}
                                        <div className="signature-display">
                                            <label>Accountant Signature:</label>
                                            {viewModal.accountantSignature ? (
                                                viewModal.accountantSignature.startsWith('data:image') ? (
                                                    <img src={viewModal.accountantSignature} alt="Accountant Signature" className="signature-image" />
                                                ) : (
                                                    <div className="signature-text-display">{viewModal.accountantSignature}</div>
                                                )
                                            ) : (
                                                <div className="signature-placeholder">No signature available</div>
                                            )}
                                        </div>
                                        
                                        {/* Receiver Signature - Only if collected */}
                                        {viewModal.status === 'collected' && (
                                            <div className="signature-display">
                                                <label>Receiver Signature:</label>
                                                {viewModal.receiverSignature ? (
                                                    <img src={viewModal.receiverSignature} alt="Receiver Signature" className="signature-image" />
                                                ) : (
                                                    <div className="signature-placeholder">No signature available</div>
                                                )}
                                            </div>
                                        )}
                                        
                                        {/* Receptionist Signature - Only if collected */}
                                        {viewModal.status === 'collected' && (
                                            <div className="signature-display">
                                                <label>Receptionist Signature:</label>
                                                {viewModal.receptionistSignature ? (
                                                    viewModal.receptionistSignature.startsWith('data:image') ? (
                                                        <img src={viewModal.receptionistSignature} alt="Receptionist Signature" className="signature-image" />
                                                    ) : (
                                                        <div className="signature-text-display">{viewModal.receptionistSignature}</div>
                                                    )
                                                ) : (
                                                    <div className="signature-placeholder">No signature available</div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Attachments Section */}
                                <div className="attachments-section">
                                    <div className="attachments-header">
                                        <h3>📎 Attachments</h3>
                                        <label className="btn btn-sm btn-secondary attachment-upload-btn" style={{ cursor: 'pointer' }}>
                                            {attachmentUploading ? 'Uploading...' : '+ Add Files'}
                                            <input
                                                ref={attachmentInputRef}
                                                type="file"
                                                multiple
                                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                                                onChange={handleAttachmentUpload}
                                                disabled={attachmentUploading}
                                                style={{ display: 'none' }}
                                            />
                                        </label>
                                    </div>

                                    {(!viewModal.documents || viewModal.documents.length === 0) ? (
                                        <div className="attachments-empty">No attachments yet</div>
                                    ) : (
                                        <div className="attachments-list">
                                            {viewModal.documents.map((doc) => {
                                                const isMissing = missingAttachments.has(doc._id);
                                                return (
                                                <div
                                                    key={doc._id}
                                                    className={`attachment-item${isMissing ? ' attachment-missing' : ''}`}
                                                >
                                                    <div className="attachment-icon">
                                                        {isMissing ? '⚠️' :
                                                         doc.fileType === 'application/pdf' ? '📄' :
                                                         doc.fileType?.startsWith('image/') ? '🖼️' :
                                                         doc.fileType?.includes('word') ? '📝' :
                                                         doc.fileType?.includes('excel') || doc.fileType?.includes('sheet') ? '📊' : '📁'}
                                                    </div>
                                                    <div className="attachment-info">
                                                        <span className="attachment-name" title={doc.fileName}>{doc.fileName}</span>
                                                        {isMissing ? (
                                                            <span className="attachment-meta attachment-missing-note">
                                                                Missing in SharePoint — deleted there directly. Restore it from the
                                                                site recycle bin, or remove this entry with 🗑️.
                                                            </span>
                                                        ) : (
                                                            <span className="attachment-meta">
                                                                {formatFileSize(doc.fileSize)}
                                                                {doc.uploadedBy && ` • ${doc.uploadedBy}`}
                                                                {doc.uploadedAt && ` • ${new Date(doc.uploadedAt).toLocaleDateString('en-GB')}`}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="attachment-actions">
                                                        <button
                                                            className="btn btn-sm btn-primary"
                                                            onClick={() => handleViewAttachment(doc)}
                                                            title={isMissing ? 'Re-check — the file may have been restored' : 'View / Download'}
                                                        >
                                                            {isMissing ? '🔄' : '👁️'}
                                                        </button>
                                                        {doc.spWebUrl && !isMissing && (
                                                            <a
                                                                className="btn btn-sm btn-secondary"
                                                                href={doc.spWebUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                title="Open in SharePoint (requires access to the Accounts site)"
                                                            >
                                                                🔗
                                                            </a>
                                                        )}
                                                        <button
                                                            className="btn btn-sm btn-danger"
                                                            onClick={() => handleDeleteAttachment(doc._id, doc.fileName)}
                                                            title={isMissing ? 'Remove this entry' : 'Delete'}
                                                        >
                                                            🗑️
                                                        </button>
                                                    </div>
                                                </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    className="btn btn-primary"
                                    onClick={() => handleDownloadPdf(viewModal)}
                                    style={{ marginRight: '10px' }}
                                    title="Open printable PDF voucher"
                                >
                                    🖨️ Print PDF
                                </button>
                                {(viewModal.status === 'returned' || viewModal.status === 'cancelled') && canApprove && (
                                    <button
                                        className="btn btn-warning"
                                        onClick={() => handleReissueCashPayment(viewModal._id)}
                                        style={{ marginRight: '10px' }}
                                    >
                                        Re-submit Cash Payment
                                    </button>
                                )}
                                {['pending', 'returned', 'collected'].includes(viewModal.status) && canApprove && (
                                    <button 
                                        className="btn btn-danger" 
                                        onClick={() => {
                                            setSelectedCashPayment(viewModal);
                                            openCancelModal(viewModal);
                                        }}
                                        style={{ marginRight: '10px' }}
                                    >
                                        Cancel Payment
                                    </button>
                                )}
                                <button className="btn btn-secondary" onClick={() => setViewModal(null)}>Close</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* RETURN MODAL */}
                {showReturnModal && selectedCashPayment && (
                    <div className="modal-overlay" onClick={closeReturnModal}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>Return Cash Payment</h2>
                                <button className="modal-close" onClick={closeReturnModal}>×</button>
                            </div>
                            <div className="modal-body">
                                {returnError && <div className="alert alert-error">{returnError}</div>}
                                
                                <div className="cheque-details-section">
                                    <h3>Cash Payment Details</h3>
                                    <div className="detail-grid">
                                        <div className="detail-item">
                                            <label>Voucher Number:</label>
                                            <span>{selectedCashPayment.voucherNumber}</span>
                                        </div>
                                        <div className="detail-item">
                                            <label>Supplier:</label>
                                            <span>{selectedCashPayment.supplierName}</span>
                                        </div>
                                        <div className="detail-item">
                                            <label>Company:</label>
                                            <span>{getShortCompanyName(selectedCashPayment.company)}</span>
                                        </div>
                                        <div className="detail-item">
                                            <label>Amount:</label>
                                            <span className="amount-highlight">{formatAmount(selectedCashPayment.amount)}</span>
                                        </div>
                                    </div>
                                </div>
                                
                                <form onSubmit={handleReturnCashPayment} className="return-form">
                                    <div className="form-group">
                                        <label>Return Reason *</label>
                                        <textarea
                                            value={returnReason}
                                            onChange={(e) => setReturnReason(e.target.value)}
                                            placeholder="Enter reason for returning this cash payment..."
                                            rows="4"
                                            required
                                            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                                        />
                                    </div>
                                    
                                    <div className="form-actions">
                                        <button type="button" onClick={closeReturnModal} className="btn btn-secondary">
                                            Cancel
                                        </button>
                                        <button type="submit" className="btn btn-warning" disabled={returnLoading}>
                                            {returnLoading ? 'Returning...' : 'Return to Accounts'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {/* CANCEL MODAL */}
                {showCancelModal && selectedCashPayment && (
                    <div className="modal-overlay" onClick={closeCancelModal}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>Cancel Cash Payment</h2>
                                <button className="modal-close" onClick={closeCancelModal}>×</button>
                            </div>
                            <div className="modal-body">
                                {cancelError && <div className="alert alert-error">{cancelError}</div>}
                                
                                <div className="cheque-details-section">
                                    <h3>Cash Payment Details</h3>
                                    <div className="detail-grid">
                                        <div className="detail-item">
                                            <label>Voucher Number:</label>
                                            <span>{selectedCashPayment.voucherNumber}</span>
                                        </div>
                                        <div className="detail-item">
                                            <label>Supplier:</label>
                                            <span>{selectedCashPayment.supplierName}</span>
                                        </div>
                                        <div className="detail-item">
                                            <label>Company:</label>
                                            <span>{getShortCompanyName(selectedCashPayment.company)}</span>
                                        </div>
                                        <div className="detail-item">
                                            <label>Amount:</label>
                                            <span className="amount-highlight">{formatAmount(selectedCashPayment.amount)}</span>
                                        </div>
                                    </div>
                                </div>
                                
                                <form onSubmit={handleCancelCashPayment} className="cancel-form">
                                    <div className="form-group">
                                        <label>Cancellation Reason *</label>
                                        <textarea
                                            value={cancellationReason}
                                            onChange={(e) => setCancellationReason(e.target.value)}
                                            placeholder="Enter reason for cancelling this cash payment..."
                                            rows="4"
                                            required
                                            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                                        />
                                    </div>
                                    
                                    <div className="form-actions">
                                        <button type="button" onClick={closeCancelModal} className="btn btn-secondary">
                                            Cancel
                                        </button>
                                        <button type="submit" className="btn btn-danger" disabled={cancelLoading}>
                                            {cancelLoading ? 'Cancelling...' : 'Cancel Payment'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Cashbook;
